// Bildschirm "Kontakt anlegen" (ST-PTY-001, CM-CAP-001).
//
// UNVERAENDERT aus main.tsx herausgeloest (ST-UX-001). Was sich aendert, ist
// ausschliesslich die Umgebung: der Bildschirm traegt keine eigene Seite mehr,
// sondern liegt im Hauptbereich des Rahmens. Seine Ueberschrift ist deshalb ein
// <h2> unter dem <h1> des Rahmens und keine zweite Seitenueberschrift.
//
// Die Basis-URL kommt als Eigenschaft herein statt aus dem Bau-Define. Der
// Grund ist der Nachweis: ein Modul, das eine zur Bauzeit ersetzte Konstante
// liest, laesst sich unter `node --test` nicht laden.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  Body1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Button,
  Caption1,
  Card,
  Field,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Select,
  Textarea,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';

import { plainMessageFor, type PlainMessage } from '../access.ts';
import {
  ADDRESS_CHOOSE_COUNTRY,
  ADDRESS_DISMISS_LABEL,
  ADDRESS_DISMISSED,
  ADDRESS_RESTORE_LABEL,
  ADDRESS_ERRORS_NEXT_STEP,
  ADDRESS_SAVED,
  ADDRESS_SAVED_LINK,
  ADDRESS_SECTION_EXPLANATION,
  ADDRESS_SECTION_HEADING,
  addressAnchor,
  addressFieldHint,
  NO_ADDRESS,
  parseAddressText,
  recognitionSummaryWithAddress,
  recognizeWithAddress,
  type AddressPasteResult,
} from '../address-paste.ts';
import {
  erkannteAngabenZahl,
  alsAngabenDerOrganisation,
  isOrgAngabePfad,
  ohneUebernommeneAngaben,
  organisationAngabenAbgelehnt,
  organisationAngabenGefunden,
  organisationsGrund,
  organisationVorschauZeilen,
  KEINE_ERKANNTEN_ANGABEN,
  organisationAngabenAusRest,
  type OrganisationAngaben,
} from '../organisation-angaben.ts';
import { OrganisationAngabenAbschnitt } from './quick-capture-organisation-angaben.tsx';
import {
  alleSichtbarZeile,
  fetchLeitungAbgleich,
  KEINE_ERKANNTE_LEITUNG,
  leitungAngelegtVorhandenTitel,
  leitungAngelegtTitel,
  leitungAnlegenLabel,
  istEndgueltigAbgelehnt,
  leitungAusRest,
  leitungEndgueltigAbgelehnt,
  leitungTeilerfolg,
  type LeitungAbgleich,
  type LeitungName,
} from '../leitung.ts';
import { LeitungAbgleichGruppe, LeitungGruppe } from './quick-capture-leitung.tsx';
import { addressesHref, countryOptions, POSTAL_CODE_REQUIRED_COUNTRY } from '../addresses.ts';
import { AppLink, navigate } from '../shell/router.tsx';
import { Segment } from '../shell/segment.tsx';
import {
  candidateLinkLabel,
  CREATE_ANYWAY_LABEL,
  CREATE_LABEL,
  DUPLICATE_HEADING,
  DUPLICATE_IS_NOT_A_BLOCKER,
  DUPLICATE_IS_NOT_A_PROOF,
  duplicateSummary,
  fetchDuplicateCandidates,
  isCheckable,
  matchReason,
  type DuplicateCandidate,
} from '../duplicate-candidates.ts';
import type { HausArt } from '../haus-art.ts';
import { fetchSearchOptions, kindLabel, lifecycleLabel, partyHref } from '../parties.ts';
import { CLIENT_REQUEST_SUPERSEDED, onlyRequestIdRejected, requestFor, type HeldRequest } from '../client-request.ts';
import { ArbeitetFuer } from './quick-capture-arbeitet-fuer.tsx';
import { roleCodeForFunction } from '../funktion-vorschlag.ts';
import { ROLE_CODES, roleLabel } from '../relationships.ts';
import { AnlegenVorschau } from './anlegen-vorschau.tsx';
import {
  applyFreeTextDraft,
  carriedOverText,
  EMPTY_DRAFT,
  FREE_TEXT_HINT,
  FREE_TEXT_LABEL,
  FREE_TEXT_PLACEHOLDER,
  functionFromDraft,
  NOTHING_RECOGNIZED,
  organisationFromDraft,
  withoutTakenOrganisation,
  withTakenFunction,
  parseFreeText,
  recognitionSummary,
  RECOGNITION_HEADING,
  UNASSIGNED_IS_NOT_A_BLOCKER,
  UNASSIGNED_REASON_TEXTS,
  unassignedLine,
  type FreeTextDraft,
  type OverridableField,
  type UnassignedFragment,
} from '../free-text.ts';
import {
  NEXT_IMPROVEMENT_LABEL,
  NOTHING_TO_IMPROVE,
  nextImprovementFor,
  type NextImprovement,
} from '../next-improvement.ts';
import { useScreenStyles } from '../shell/screen-styles.ts';
import {
  EMPTY_FORM,
  inputIdFor,
  bothSavedLine,
  createActionLabel,
  buildRequestBody,
  CAPTURE_REQUEST_REUSED,
  CAPTURE_REQUEST_SUPERSEDED,
  createdBothTitle,
  organizationForm,
  organizationInvalid,
  organizationRequestSuperseded,
  partialCaptureMessage,
  partialUncertainMessage,
  captureRequestKey,
  CAPTURE_UNREACHABLE,
  organizationUnreachable,
  takenSuggestion,
  type PartialCaptureMessage,
  submitQuickCapture,
  type QuickCaptureResult,
  WORKS_FOR_REJECTIONS,
  captureVisibilityNotice,
  capturedVisibilityLine,
  HOUSE_AMBIGUOUS_CAPTURE_MESSAGE,
  MULTI_HOUSE_CAPTURE_NOTICE,
  worksForSaved,
  type WorksForChoice,
  validationMessageFor,
  validationSummary,
  type AddressFields,
  type FieldError,
  type PartyKind,
  type QuickCaptureForm,
  clearWorksForPreset,
  peekWorksForPreset,
} from '../quick-capture.ts';
import { VorgangNach } from '../shell/zustand.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';

/**
 * Die Flaechen des Freitextteils.
 *
 * LOKAL UND NICHT IN screen-styles.ts, und das ist keine Nachlaessigkeit: die
 * geteilte Datei traegt, was MEHRERE Bildschirme gemeinsam haben -- genau das
 * ist ihre Begruendung. Ein Erkennungsblock, den es genau einmal gibt, waere
 * dort ein geteiltes Muster, das nichts teilt.
 */
const useAnlegenStyles = makeStyles({
  // v4 .df: Abbrechen und Anlegen am Ende der Karte, rechts; die Hauptaktion zuletzt.
  fuss: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    marginTop: '0.5rem',
    // Bis 640 px bleibt die Hauptaktion am unteren Rand sichtbar (Michael B 12, DESIGN zu #548);
    // was sie verdecken koennte, haelt scrollPaddingBottom frei (WCAG 2.4.11).
    '@media (max-width: 640px)': {
      position: 'sticky',
      bottom: 0,
      zIndex: 1,
      paddingBlock: '0.75rem',
      backgroundColor: tokens.colorNeutralBackground1,
      borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    },
  },
  art: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  artFehler: { color: tokens.colorPaletteRedForeground1 },
  // Fluents Card schneidet mit overflow:hidden ab; damit haelt sich eine klebende Fusszeile an der
  // Karte statt am Fenster fest.
  karte: { '@media (max-width: 640px)': { overflow: 'visible' } },
  // Die Spalte darf nie breiter als die Zeile werden: 12rem sind bei 200 % Textzoom 384 px, mehr
  // als 320 (WCAG 1.4.10). Die Karte schnitt das bisher ab; seit sie fuer die klebende Fusszeile
  // nicht mehr abschneidet, waere es Ueberlauf.
  // Ein natives select ist so breit wie sein laengster Eintrag; hier nie breiter als die Zeile
  // (Land und Funktion).
  auswahl: { maxWidth: '100%', minWidth: 0, '& select': { maxWidth: '100%' } },
  zeile: { gridTemplateColumns: 'repeat(auto-fit, minmax(min(12rem, 100%), 1fr))' },
  hausSatz: { color: tokens.colorNeutralForeground2, textAlign: 'end' },
  // Satz und Rueckweg in einer Zeile, bei 320 px untereinander.
  abgelehnt: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: tokens.spacingHorizontalS },
  verborgen: { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
  // Ein Bruchstueck kann ein einziges langes Wort sein; es bricht, statt die Seite zu verbreitern.
  details: { overflowWrap: 'anywhere', '& > summary': { cursor: 'pointer', color: tokens.colorNeutralForeground2 } },
});

const ART_OPTIONEN = [
  { id: 'person', label: 'Person' },
  { id: 'organization', label: 'Organisation' },
] as const;

const useFreeTextStyles = makeStyles({
  // Volle Breite statt einer Pixelbreite: bei 320 CSS-Pixeln und bei 200
  // Prozent Textzoom bleibt es dieselbe Spalte (CM-UX-001, WCAG 1.4.10).
  area: { width: '100%' },
  recognition: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    marginTop: '0.5rem',
  },
  heading: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  list: {
    margin: 0,
    // Die Aufzaehlungszeichen bleiben im Bild; `paddingLeft` in rem waechst mit
    // dem Textzoom mit, eine Pixelangabe taete das nicht.
    paddingInlineStart: '1.25rem',
  },
  // Umbruch statt Querscrollen: ein eingefuegtes Bruchstueck kann beliebig lang
  // sein, und ein waagerechter Balken bei 320 Pixeln waere ein Reflowfehler.
  fragment: { overflowWrap: 'anywhere' },
  // Die Kandidatenliste der Dublettenpruefung. Dieselbe Begruendung wie oben:
  // sie gehoert genau einem Bildschirm und nicht in die geteilte Datei.
  candidates: {
    margin: '0.5rem 0 0',
    paddingInlineStart: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  // Ein langer Anzeigename bricht um, statt einen waagerechten Balken zu
  // erzeugen (CM-UX-001, WCAG 1.4.10).
  candidate: { overflowWrap: 'anywhere' },
});

/**
 * Die DOM-Kennung der EINEN Statusregion dieses Bildschirms.
 *
 * SIE BLEIBT DIE EINE, und das ist die tragende Entscheidung der
 * Dublettenpruefung an der Oberflaeche. Die Warnung ueber moegliche Dubletten
 * ist eine Statusmeldung wie die Bestaetigung und die Ablehnung; sie bekommt
 * deshalb KEINE zweite Region mit `role="status"`. Zwei Regionen desselben
 * Rollennamens machen "die Statusmeldung" mehrdeutig -- fuer Hilfsmittel wie
 * fuer die Proben, die sie halten -- und ein Screenreader saehe sich zwei
 * Stellen gegenueber, an denen das Ergebnis derselben Handlung stehen kann.
 *
 * Der Erkennungsblock unter dem Freitextfeld traegt aus demselben Grund seit
 * jeher `aria-live` OHNE `role="status"`: er spricht ueber die EINGABE, diese
 * Region ueber das ERGEBNIS.
 *
 * `tabIndex={-1}`: nicht in der Tabreihenfolge, aber programmatisch
 * fokussierbar. Genau das braucht ein Fokusziel -- erreichbar, wenn der Fokus
 * es braucht, und niemandem im Weg, der sich durchtabbt.
 */
const STATUS_REGION_ID = 'qc-status';

/** Gescheitert vor dem ersten Schreibschritt: nichts ist unterwegs, und die Eingabe bleibt stehen. */
const NICHT_ERREICHBAR_VOR_ANLAGE: PlainMessage = (() => {
  const satz = plainMessageFor(undefined, { status: 0, answered: false, hasCorrelationId: false });
  return Object.freeze({ ...satz, explanation: `${satz.explanation} Deine Eingabe steht noch im Formular.` });
})();

/** Die Laender der Auswahl -- einmal, nicht bei jedem Rendern. */
const COUNTRIES = countryOptions();

/**
 * Die Schnellerfassung (ST-PTY-001, CM-CAP-001).
 *
 * WAS SIE BEWUSST NICHT HAT, und jedes davon aus einem benannten Grund:
 *
 *   * KEIN VERLAGSFELD. "Fuer welchen Verlag legst du diesen Kontakt an?" ist
 *     im Anlageformular naheliegend und waere genau die Zuordnung aus
 *     `CM-PTY-010`, die `ST-PTY-002` traegt. Die Story fuehrt das als
 *     ausdrueckliches Nichtziel.
 *
 *     Hier stand bis zum 29.08.2026 "die Zuordnung, die DEC-012 bis zu ihrer
 *     Entscheidung vertagt hat". `DEC-012` ist seit ADR-0029 (25.08.2026)
 *     entschieden; das Nichtziel bleibt, sein Grund ist ein anderer.
 *   * KEINE NAMENSAEHNLICHKEIT IN DER DUBLETTENPRUEFUNG. Seit dem 01.09.2026
 *     prueft der Bildschirm vor der Anlage auf einen EXAKTEN Treffer der
 *     E-Mail-Adresse oder der Rufnummer (`ST-DQ-001`, `CM-CAP-005`). Aehnliche
 *     NAMEN erkennt er nicht: `CM-SEA-002` verlangt Fehlertoleranz gegen ein
 *     abgestimmtes Referenzkorpus, und es gibt keins -- eine Naeherung waere
 *     eine Zusage, die der Code nicht haelt.
 *   * KEIN PFLICHTFELD OHNE ZWECK. CM-CAP-002 sagt es woertlich: "kein Feld
 *     ohne unmittelbaren Zweck ist initial Pflicht". Pflicht ist nur, was ohne
 *     Ersatz nicht erfindbar ist -- die Art und ein Name.
 */
function TeilerfolgLeiste({ meldung }: { readonly meldung: PartialCaptureMessage }): ReactElement {
  return (
    <MessageBar intent="warning">
      <MessageBarBody>
        <MessageBarTitle>{meldung.headline}</MessageBarTitle>
        {meldung.reason.map((satz) => (
          <div key={satz}>{satz}</div>
        ))}
        <div>{meldung.nextStep}</div>
      </MessageBarBody>
    </MessageBar>
  );
}

export function QuickCaptureScreen({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
}: {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
}): ReactElement {
  const styles = useScreenStyles();
  const freeTextStyles = useFreeTextStyles();
  const anlegenStyles = useAnlegenStyles();
  const fussRef = useRef<HTMLDivElement | null>(null);
  // Die klebende Fusszeile darf ein fokussiertes Element nie verdecken (WCAG 2.4.11): der Scrollbereich
  // haelt unten genau ihre Hoehe frei, solange sie klebt, auch wenn sie umbricht.
  useEffect(() => {
    const fuss = fussRef.current;
    if (fuss === null || typeof ResizeObserver === 'undefined') return undefined;
    // Beide Scrollbereiche: im festen App-Rahmen scrollt der Hauptbereich, darunter das Dokument.
    const bereiche = [document.documentElement, document.getElementById('hauptbereich')].filter((e): e is HTMLElement => e !== null);
    const anwenden = (): void => {
      const frei = getComputedStyle(fuss).position === 'sticky' ? `${fuss.getBoundingClientRect().height + 8}px` : '';
      for (const bereich of bereiche) bereich.style.scrollPaddingBottom = frei;
    };
    anwenden();
    const beobachter = new ResizeObserver(anwenden);
    beobachter.observe(fuss);
    window.addEventListener('resize', anwenden);
    return () => {
      beobachter.disconnect();
      window.removeEventListener('resize', anwenden);
      for (const bereich of bereiche) bereich.style.scrollPaddingBottom = '';
    };
  }, []);
  const [form, setForm] = useState<QuickCaptureForm>(EMPTY_FORM);
  /**
   * Der freie Text und seine Zerlegung (CM-CAP-001).
   *
   * Der Entwurf steht als eigener Zustand und wird NICHT beim Rendern neu
   * berechnet. Der Grund ist die Art des Kontakts: sobald die Nutzerin sie
   * selbst gewaehlt hat, geht sie als Vorgabe in die Zerlegung ein -- eine
   * Berechnung beim Rendern muesste dieselbe Fallunterscheidung ein zweites
   * Mal treffen, und zwei Stellen, die dasselbe entscheiden, entscheiden es
   * irgendwann verschieden.
   */
  const [freeText, setFreeText] = useState('');
  const [draft, setDraft] = useState<FreeTextDraft>(EMPTY_DRAFT);
  /**
   * Was die Nutzerin selbst angefasst hat.
   *
   * Ohne diese Menge waere "korrigierbar" eine Falle -- die Begruendung steht
   * bei `applyFreeTextDraft` in free-text.ts.
   */
  const [overridden, setOverridden] = useState<ReadonlySet<OverridableField>>(() => new Set());
  /**
   * Die Anschrift aus dem Freitext ("Anschrift einfuegen", 29.09.2026).
   *
   * `addressTouched`: ein Anschriftfeld gehoert der Nutzerin, sobald sie es
   * anfasst -- dieselbe Regel wie `overridden` fuer die uebrigen Felder, als
   * EIN Kennzeichen fuer die ganze Anschrift, weil ihre Zeilen zusammen eine
   * Aussage machen. `dismissedAnchor`: "Anschrift nicht uebernehmen" gilt fuer
   * DIESE Anschrift (ihre Ortszeile) -- eine andere eingefuegte Anschrift ist
   * ein neuer Vorschlag, ein geleerter Freitext hebt die Ablehnung auf.
   */
  const [addressPaste, setAddressPaste] = useState<AddressPasteResult>(NO_ADDRESS);
  const [addressTouched, setAddressTouched] = useState(false);
  const [dismissedAnchor, setDismissedAnchor] = useState<string | undefined>();
  const addressDismissed = dismissedAnchor !== undefined;
  /** Die Adressseite der eben angelegten Party, wenn eine Anschrift mitgespeichert wurde. */
  const [createdAddressOf, setCreatedAddressOf] = useState<string | undefined>();
  /** Haengt die gespeicherte Anschrift an der neuen Organisation, ihr Name fuer den Link (DESIGN zu #773). */
  const [createdAddressOrganisation, setCreatedAddressOrganisation] = useState<string | undefined>();
  /** Ein Fokusziel ausserhalb der Statusregion, nach dem naechsten Rendern (wie `focusStatus`). */
  const focusTarget = useRef<string | undefined>(undefined);
  /**
   * Was bei der gerade erfolgten Anlage NICHT mitgespeichert wurde.
   *
   * Eigener Zustand aus demselben Grund wie `improvement`: das Formular und der
   * Freitext werden nach dem 201 geleert, und mit ihnen der Rest. Ohne diesen
   * Zustand verschwaende genau in dem Augenblick die Auskunft, auf die es
   * ankommt.
   */
  const [carried, setCarried] = useState<string | undefined>();
  const [errors, setErrors] = useState<readonly FieldError[]>([]);
  const [rejection, setRejection] = useState<PlainMessage | undefined>();
  const [created, setCreated] = useState<string | undefined>();
  /**
   * Name und Art des eigenen Hauses fuer den Hinweis "zunaechst nur fuer ...
   * sichtbar" -- nur, wenn es genau eines gibt; ohne Namen nennt der Satz die
   * Art ("deinen Verlag"). Ein gescheiterter Abruf aendert an der Anlage nichts.
   */
  const [houseName, setHouseName] = useState<string | undefined>();
  const [houseArt, setHouseArt] = useState<HausArt | null | undefined>();
  // 'laedt', solange der Kontext unterwegs ist: dann steht KEIN Haus-Satz da statt eines,
  // der fuer Personen mit mehreren Haeusern falsch waere (Codex-P2 zu #548).
  const [hauslage, setHauslage] = useState<'laedt' | 'eins' | 'mehrere'>('laedt');
  useEffect(() => {
    let current = true;
    void (async () => {
      try {
        const token = await accessToken();
        if (token === undefined) return;
        const options = await fetchSearchOptions({ baseUrl: apiBaseUrl, accessToken: token });
        if (current && options.outcome === 'loaded') {
          if (options.operatingContexts.length === 1) {
            setHouseName(options.operatingContexts[0]?.displayName);
            setHouseArt(options.operatingContexts[0]?.kind);
          }
          setHauslage(options.operatingContexts.length > 1 ? 'mehrere' : 'eins');
        } else if (current) {
          setHauslage('eins');
        }
      } catch {
        // Ohne Namen und Art bleibt der Satz bei "deinen Verlag".
        if (current) setHauslage('eins');
      }
    })();
    return () => {
      current = false;
    };
  }, [accessToken, apiBaseUrl]);
  /**
   * Die gewaehlte Organisation ("arbeitet für", ST-REL-001) -- NEBEN dem
   * Formular, weil der Freitext das Formular neu schreibt und eine Wahl aus
   * der Suche nie aus dem Text entsteht.
   */
  // Aus "Ansprechpartner anlegen" an einer Organisation vorbelegt (Slice 6b); die
  // Vorbelegung gilt fuer genau diese eine Erfassung und wird danach vergessen.
  const [worksFor, setWorksFor] = useState<WorksForChoice | undefined>(() => peekWorksForPreset());
  // Wer von einer Organisation kam, kehrt mit "Abbrechen" dorthin zurueck, nicht zur Liste.
  const [anfangsOrganisation] = useState<WorksForChoice | undefined>(() => peekWorksForPreset());
  const [verwerfenFragen, setVerwerfenFragen] = useState(false);
  const abbrechenRef = useRef<HTMLButtonElement | null>(null);
  const [rueckweg] = useState<string>(() => {
    const vorbelegt = peekWorksForPreset();
    return vorbelegt === undefined ? '/kontakte' : partyHref(vorbelegt.partyId);
  });
  useEffect(() => {
    clearWorksForPreset();
  }, []);
  const [createdWorksFor, setCreatedWorksFor] = useState<string | undefined>();
  /** Der Name, der als neue Organisation mitentsteht (Kaestchen unter "Arbeitet für"). */
  const [neueOrganisation, setNeueOrganisation] = useState<string | undefined>();
  /** Was die Vorschau als Organisation zeigt: gewaehlt (vorhanden) oder mitangelegt (neu); sonst keine Vorschau. */
  const vorschauOrganisation: { readonly name: string; readonly neu: boolean } | undefined =
    form.kind !== 'person'
      ? undefined
      : worksFor !== undefined
        ? { name: worksFor.displayName, neu: false }
        : neueOrganisation !== undefined
          ? { name: neueOrganisation, neu: true }
          : undefined;
  /**
   * Die Funktion an "Arbeitet für" (S1b): vorgeschlagen aus dem Wortlaut der Signatur, bis die
   * Person selbst waehlt. Ihre Wahl gilt fuer DIESEN Wortlaut; ein anderer eingefuegter Text
   * bringt einen neuen Vorschlag.
   */
  const [funktionWahl, setFunktionWahl] = useState<{ readonly fuer: string | undefined; readonly code: string | undefined } | undefined>();
  const erkannteFunktion = functionFromDraft(draft);
  const vorgeschlageneFunktion = roleCodeForFunction(erkannteFunktion);
  const funktion = funktionWahl !== undefined && funktionWahl.fuer === erkannteFunktion ? funktionWahl.code : vorgeschlageneFunktion;
  /** Gesendet und angezeigt wird sie nur mit einer Organisation; ohne sie lehnte der Server ab. */
  const funktionGilt = vorschauOrganisation === undefined ? undefined : funktion;
  /** Die neue Organisation entsteht mit dieser Anlage (Karte "Neu"); nur dann gibt es ihre Angaben (S2). */
  const orgNeu = form.kind === 'person' && worksFor === undefined && neueOrganisation !== undefined;
  /**
   * Website, Handelsregister und USt-IdNr. der neuen Organisation (S2): vorbefuellt aus dem
   * Rest, bis die Nutzerin ein Feld aendert; ab dann gehoeren sie ihr wie die Anschrift.
   */
  const [orgAngabenEigen, setOrgAngabenEigen] = useState<OrganisationAngaben | undefined>();
  /**
   * Die Angaben der Signatur gehoeren der Organisation, deren Namen sie traegt (Codex-P1 #773):
   * eine andere neue Organisation unter "Arbeitet für" bekommt weder ihre Kennungen noch ihre
   * Anschrift; die Anschrift bleibt dann bei der Person wie ohne Organisation.
   */
  const orgAusSignatur = orgNeu && neueOrganisation !== undefined && neueOrganisation.trim() === organisationFromDraft(draft)?.trim();
  const erkannteOrgAngabenAlle = useMemo(() => organisationAngabenAusRest(draft.unassigned), [draft]);
  const erkannteOrgAngaben = orgAusSignatur ? erkannteOrgAngabenAlle : KEINE_ERKANNTEN_ANGABEN;
  const orgAngaben = orgAngabenEigen ?? erkannteOrgAngaben.angaben;
  const orgAngabenZahl = orgAusSignatur ? erkannteAngabenZahl(erkannteOrgAngaben.angaben, form.address !== undefined) : 0;
  // Eine andere neue Organisation beginnt mit eigenen, leeren Angaben; nichts wird mitgenommen.
  useEffect(() => {
    setOrgAngabenEigen(undefined);
    setOrgAngabenOffen(undefined);
    // Nach dem Teilerfolg wird aus "Neu" die angelegte Organisation: die Wahl der Leitung bleibt.
    if (neueOrganisation === undefined && angelegteOrganisation !== undefined) return;
    setLeitungGewaehlt(new Set());
  }, [neueOrganisation]);
  /** Offen, sobald etwas erkannt ist (V1); eine Ablehnung am Feld oeffnet ihn (V4). */
  const [orgAngabenOffen, setOrgAngabenOffen] = useState<boolean | undefined>();
  const angabenOffen = orgAngabenOffen ?? orgAngabenZahl > 0;
  /**
   * Die Leitung der neuen Organisation (S3): erkannt aus dem Rest, nur bei der Organisation
   * der Signatur, und jedes Haekchen ist zunaechst aus (Entscheidung Dirigent).
   */
  /** Die in dieser Erfassung angelegte Organisation, solange die Person noch fehlt. */
  const [angelegteOrganisation, setAngelegteOrganisation] = useState<WorksForChoice | undefined>();
  // Auch nach dem Teilerfolg O4: die Organisation steht dann schon und ist gewaehlt, der zweite
  // Versuch legt Kontakt und die gewaehlte Leitung an.
  const orgDieserErfassung = form.kind === 'person' && worksFor !== undefined && angelegteOrganisation?.partyId === worksFor.partyId;
  const leitungFuerNeue = orgAusSignatur || (orgDieserErfassung && takenSuggestion(worksFor, organisationFromDraft(draft)) !== undefined);
  const erkannteLeitung = useMemo(
    () => (leitungFuerNeue ? leitungAusRest(draft.unassigned, { givenName: form.givenName, familyName: form.familyName }) : KEINE_ERKANNTE_LEITUNG),
    [leitungFuerNeue, draft, form.givenName, form.familyName],
  );
  const [leitungGewaehlt, setLeitungGewaehlt] = useState<ReadonlySet<string>>(() => new Set());
  const gewaehlteLeitung = erkannteLeitung.namen.filter((name) => leitungGewaehlt.has(name.anzeige));
  /** Was aus dem Rest in Felder oder Karte der neuen Organisation ging; es fehlt unter "Nicht übernommen" (X6). */
  const uebernommenFuerOrganisation = [...erkannteOrgAngaben.taken, ...erkannteLeitung.taken];
  /**
   * Der Abgleich an einer VORHANDENEN Organisation der Signatur (V8): der Server vergleicht die
   * erkannten Namen mit ihrer laufenden Leitung (#791), das Web faltet nicht. Ist die
   * Organisation nicht sichtbar oder keine (404), steht keine Gruppe da, und die Zeile bleibt im Rest.
   */
  const orgVorhandenAusSignatur =
    form.kind === 'person' && worksFor !== undefined && !orgDieserErfassung && takenSuggestion(worksFor, organisationFromDraft(draft)) !== undefined;
  const leitungLaut = useMemo(
    () => (orgVorhandenAusSignatur ? leitungAusRest(draft.unassigned, { givenName: form.givenName, familyName: form.familyName }) : KEINE_ERKANNTE_LEITUNG),
    [orgVorhandenAusSignatur, draft, form.givenName, form.familyName],
  );
  const abgleichSchluessel =
    worksFor !== undefined && leitungLaut.namen.length > 0 ? `${worksFor.partyId}|${leitungLaut.namen.map((name) => name.anzeige).join('|')}` : undefined;
  const [abgleich, setAbgleich] = useState<
    { readonly schluessel: string; readonly zustand: 'laedt' | 'gescheitert' | 'keiner' | LeitungAbgleich } | undefined
  >();
  useEffect(() => {
    setLeitungGewaehlt(new Set());
    if (abgleichSchluessel === undefined || worksFor === undefined) return;
    let gilt = true;
    const organisationId = worksFor.partyId;
    const namen = leitungLaut.namen;
    setAbgleich({ schluessel: abgleichSchluessel, zustand: 'laedt' });
    void (async () => {
      const token = await accessToken().catch(() => undefined);
      const ergebnis =
        token === undefined
          ? ({ outcome: 'failed' } as const)
          : await fetchLeitungAbgleich({ baseUrl: apiBaseUrl, accessToken: token, organisationId, namen }).catch(() => ({ outcome: 'failed' }) as const);
      if (!gilt) return;
      setAbgleich({
        schluessel: abgleichSchluessel,
        zustand: ergebnis.outcome === 'loaded' ? ergebnis.abgleich : ergebnis.outcome === 'not-found' ? 'keiner' : 'gescheitert',
      });
    })();
    return () => {
      gilt = false;
    };
  }, [abgleichSchluessel]);
  const abgleichZustand = abgleich !== undefined && abgleich.schluessel === abgleichSchluessel ? abgleich.zustand : undefined;
  const gewaehlteLeitungVorhanden =
    typeof abgleichZustand === 'object' ? abgleichZustand.neu.filter((name) => leitungGewaehlt.has(name.anzeige)) : [];
  /** Steht die Gruppe "Leitung laut Signatur", ist die Zeile dort und nicht im Rest (X6). */
  const leitungVorhandenTaken = abgleichZustand !== undefined && abgleichZustand !== 'keiner' ? leitungLaut.taken : [];
  /**
   * Beim Absenden ist die Zeile nur erledigt, wenn der Vergleich fertig ist. Laeuft er noch oder
   * scheiterte er, war nichts waehlbar; die Namen gehoeren dann in den Rest (Codex-P1 #800).
   */
  const leitungVorhandenErledigt = typeof abgleichZustand === 'object' ? leitungLaut.taken : [];
  /** Das Feld, das nach einer Ablehnung den Fokus bekommt, sobald es (im geoeffneten Abschnitt) steht. */
  const [fokusFeld, setFokusFeld] = useState<string | undefined>();
  useEffect(() => {
    if (fokusFeld === undefined) return;
    // Das Panel des eben geoeffneten Abschnitts haengt Fluent erst nach dem Commit ein; gewartet wird wenige Frames.
    let rahmen = 0;
    let frame = 0;
    const versuchen = (): void => {
      const feld = document.getElementById(fokusFeld);
      if (feld !== null || rahmen >= 10) {
        feld?.focus();
        setFokusFeld(undefined);
        return;
      }
      rahmen += 1;
      frame = requestAnimationFrame(versuchen);
    };
    versuchen();
    return () => cancelAnimationFrame(frame);
  }, [fokusFeld, angabenOffen]);
  /** Zaehlt geleerte Formulare; "Arbeitet für" beginnt mit jeder Runde zugeklappt. */
  const [arbeitetFuerRunde, setArbeitetFuerRunde] = useState(0);
  /** Bei "Angelegt: <Person> und <Organisation>" die Organisation, verlinkt im Satz der Beziehung. */
  const [createdOrganisation, setCreatedOrganisation] = useState<WorksForChoice | undefined>();
  /** Organisation angelegt, Person nicht (Vorgabe DESIGN O4). */
  const [teilerfolg, setTeilerfolg] = useState<
    | {
        readonly organisation: WorksForChoice;
        readonly grund: PlainMessage | undefined;
        readonly eigenerSchritt: boolean;
        readonly markiert?: number;
        /** Die Antwort zur Person kam nicht an: ob sie angelegt ist, ist offen. */
        readonly offen?: boolean;
      }
    | undefined
  >();
  /** Die gehaltenen Absendekennungen der beiden Aufrufe (ADR-0060), je eine eigene. */
  const organisationAnfrage = useRef<HeldRequest | undefined>(undefined);
  const personAnfrage = useRef<HeldRequest | undefined>(undefined);
  /** Je Person der Leitung eine eigene Absendekennung, geschluesselt nach Organisation und Name. */
  const leitungAnfragen = useRef(new Map<string, HeldRequest>());
  /** Wie viele Personen der Leitung mit dieser Erfassung entstanden sind (Bestaetigung, Abschnitt 5). */
  const [createdLeitung, setCreatedLeitung] = useState(0);
  /** Kontakt und Organisation stehen, diese Personen der Leitung noch nicht (Teilerfolg S3). */
  const [leitungRest, setLeitungRest] = useState<
    | { readonly organisation: WorksForChoice; readonly organisationNeu: boolean; readonly namen: readonly LeitungName[]; readonly offen: boolean }
    | undefined
  >();
  /** Endgueltig abgelehnt (B15): eine eigene, rote Leiste ohne Wiederholknopf. */
  const [leitungAbgelehnt, setLeitungAbgelehnt] = useState<
    { readonly organisation: WorksForChoice; readonly organisationNeu: boolean; readonly namen: readonly string[] } | undefined
  >();
  /**
   * Die naechste sinnvolle Verbesserung DES GERADE ANGELEGTEN ENTWURFS.
   *
   * Eigener Zustand und nicht aus `form` abgeleitet: das Formular wird nach dem
   * 201 geleert, damit die naechste Erfassung sofort beginnen kann. Wuerde der
   * Vorschlag beim Rendern aus `form` berechnet, waere er im selben Augenblick
   * verschwunden, in dem er entsteht -- oder das Formular duerfte nicht mehr
   * geleert werden, und das waere die schlechtere Wahl.
   */
  const [improvement, setImprovement] = useState<NextImprovement | undefined>();
  const [busy, setBusy] = useState(false);
  /**
   * Die Kandidaten der Dublettenpruefung (ST-DQ-001, CM-CAP-005).
   *
   * Eigener Zustand und nicht aus `form` abgeleitet: sie sind das Ergebnis
   * einer Abfrage gegen den Bestand und keine Eigenschaft des Formulars. Sie
   * werden geleert, sobald die Nutzerin irgendetwas aendert -- ein Kandidat zu
   * einer Adresse, die nicht mehr im Feld steht, waere eine falsche Auskunft.
   */
  const [candidates, setCandidates] = useState<readonly DuplicateCandidate[]>([]);
  const [candidatesHasMore, setCandidatesHasMore] = useState(false);
  /**
   * Die Pruefung selbst ist gescheitert.
   *
   * EIGENER ZUSTAND UND NICHT "keine Kandidaten". Eine gescheiterte Pruefung
   * als Entwarnung zu behandeln waere die schlimmste Auskunft dieses
   * Bildschirms: die Nutzerin legte an, weil niemand widersprochen hat, und
   * niemand hat nachgesehen.
   */
  const [uncheckable, setUncheckable] = useState<PlainMessage | undefined>();
  /**
   * Die Nutzerin hat die Warnung gesehen.
   *
   * Das ist das "begruendet neu anlegen" aus `CM-CAP-005`, so weit dieser
   * Schnitt es halten kann: eine bewusste, informierte zweite Handlung. Eine
   * getippte BEGRUENDUNG waere heute eine Attrappe (ADR-0002) -- sie haette
   * keinen Ort. `governance.entity_resolution_case.resolution_reason` und der
   * Status `resolved_override` aus DEC-006 setzen einen persistierten Fall
   * voraus, und den legt dieser Schnitt bewusst nicht an (ADR-0044 Ziffer 2).
   * Ein Feld, dessen Inhalt niemand liest, ist schlechter als keines.
   */
  const [warningSeen, setWarningSeen] = useState(false);
  /**
   * Der Fokus soll nach dem naechsten Rendern in die Statusregion.
   *
   * IN EINEN EFFEKT UND NICHT IN DAS `then` DES AUFRUFS -- die Lehre aus
   * Befund 2 des Zweitreviews an PR #204: im `then` ist der Zustand gesetzt,
   * aber React hat noch nicht neu gerendert, und der Fokus trifft einen
   * Bildschirm, den es gleich nicht mehr gibt.
   */
  const focusStatus = useRef(false);
  /**
   * Die Laufnummer der Dublettenpruefung -- der Waechter gegen eine VERALTETE
   * Antwort.
   *
   * BEFUND DES ZWEITREVIEWS AN #208, und es war die schlimmste Auspraegung,
   * die dieser Slice haben kann. Aendert die Nutzerin das Formular, waehrend
   * die Pruefung laeuft, raeumt `update()` zwar den vorigen Stand ab -- die
   * ALTE Antwort kam danach trotzdem an und setzte `candidates` UND
   * `warningSeen` fuer die ALTEN Werte. Der naechste Druck uebersprang damit
   * die Pruefung der AKTUELLEN Adresse und legte sofort an: eine
   * Dublettenpruefung, die genau dann nicht prueft, wenn sich der Wert
   * geaendert hat.
   *
   * EINE LAUFNUMMER UND KEIN VERGLEICH DER FORMULARWERTE -- dieselbe Bauart
   * wie `laufRef` in kontakt.tsx und aus demselben Grund: ein Wertevergleich
   * liesse zwei Antworten auf DENSELBEN Wert weiterhin um den Zustand ringen,
   * etwa wenn jemand zweimal hintereinander absendet. Nur der juengste Lauf
   * darf schreiben.
   *
   * Entwertet wird in `forgetDuplicateCheck()` -- also an genau der Stelle, an
   * der jede Aenderung ohnehin vorbeikommt. Eine zweite Aufraeumstelle waere
   * die, die irgendwann fehlt.
   */
  const checkRun = useRef(0);

  useEffect(() => {
    if (focusTarget.current !== undefined) {
      const target = focusTarget.current;
      focusTarget.current = undefined;
      document.getElementById(target)?.focus();
    }
    if (!focusStatus.current) {
      return;
    }
    focusStatus.current = false;
    document.getElementById(STATUS_REGION_ID)?.focus();
  });

  /**
   * Die Dublettenpruefung vergessen.
   *
   * EINE STELLE UND NICHT DREI: der Zustand besteht aus drei Teilen, die immer
   * gemeinsam gelten oder gemeinsam veralten. Drei einzelne Aufraeumzeilen an
   * drei Stellen waeren die Gelegenheit, an der eine davon irgendwann fehlt --
   * und die fehlende waere `warningSeen`, also ausgerechnet die, die die
   * naechste Anlage ungeprueft durchliesse.
   */
  const forgetDuplicateCheck = useCallback((): void => {
    // Der laufende Abruf darf zu Ende laufen -- er darf nur nichts mehr
    // schreiben. Dieselbe Semantik wie das Aufraeumen des Effekts in
    // kontakt.tsx.
    checkRun.current += 1;
    setCandidates([]);
    setCandidatesHasMore(false);
    setUncheckable(undefined);
    setWarningSeen(false);
  }, []);

  const update = useCallback(
    (patch: Partial<QuickCaptureForm>): void => {
      // Jedes angefasste Feld gehoert ab jetzt der Nutzerin; der Freitext
      // schreibt es nicht mehr.
      const touched = new Set<OverridableField>([...overridden, ...(Object.keys(patch) as OverridableField[])]);
      setOverridden(touched);
      if (patch.kind !== undefined && freeText.trim().length > 0) {
        // Die Art des Kontakts aendert die Zerlegung selbst: was zur neuen Art
        // nicht passt, wird nicht still fallen gelassen, sondern erscheint mit
        // seinem Grund im Rest (CM-CAP-001).
        const kind = patch.kind;
        const reparsed = recognizeWithAddress(freeText, (text) => parseFreeText(text, kind), addressDismissed).draft;
        setDraft(reparsed);
        setForm((current) => ({ ...applyFreeTextDraft(reparsed, { ...current, ...patch }, touched), address: current.address }));
      } else {
        setForm((current) => ({ ...current, ...patch }));
      }
      // Die Bestaetigung gehoert dem VORIGEN Entwurf. Sobald die Nutzerin den
      // naechsten tippt, ist sie veraltet -- und mit ihr der Vorschlag, denn der
      // spricht ueber Felder. "Diesem Entwurf fehlt noch ein Kontaktweg", waehrend
      // im Formular schon eine E-Mail steht, waere eine falsche Auskunft ueber
      // das, was gerade auf dem Bildschirm sichtbar ist.
      //
      // Der Fehlerzustand wird hier ABSICHTLICH nicht geleert: er ist am Feld
      // verankert und die Nutzerin korrigiert gerade genau dieses Feld. Ihn beim
      // ersten Tastendruck zu entfernen naehme ihr den Hinweis, dem sie folgt
      // (NFR-UX-005).
      setCreated(undefined);
      setCreatedAddressOf(undefined);
      setCreatedWorksFor(undefined);
      setImprovement(undefined);
      setCarried(undefined);
      // Die Dublettenwarnung gehoert den WERTEN, die geprueft wurden. Sobald
      // die Nutzerin irgendetwas aendert, spricht sie ueber einen Entwurf, den
      // es nicht mehr gibt -- und die Freigabe, die sie erteilt hat, galt
      // diesem alten Entwurf. Beides zu behalten hiesse, die naechste Anlage
      // ungeprueft durchzulassen, weil die vorige geprueft war.
      forgetDuplicateCheck();
    },
    [addressDismissed, forgetDuplicateCheck, freeText, overridden],
  );

  /**
   * Ein Anschriftfeld wurde geaendert. Ab jetzt gehoert die Anschrift der
   * Nutzerin; der Freitext schreibt sie nicht mehr um.
   */
  const updateAddress = useCallback(
    (patch: Partial<AddressFields>): void => {
      setAddressTouched(true);
      setForm((current) => (current.address === undefined ? current : { ...current, address: { ...current.address, ...patch } }));
      setCreated(undefined);
      setCreatedAddressOf(undefined);
      setImprovement(undefined);
      setCarried(undefined);
      // Wie jede andere Aenderung: ein laufendes Absenden spricht ab jetzt
      // ueber eine Anschrift, die nicht mehr im Formular steht, und darf die
      // korrigierte Eingabe weder leeren noch als angelegt melden
      // (Codex-Cloud-Befund an #280).
      forgetDuplicateCheck();
    },
    [forgetDuplicateCheck],
  );

  /**
   * "Anschrift nicht uebernehmen": der Abschnitt verschwindet, die Zeilen
   * gehen zurueck in den Rest des Freitexts -- sichtbar und nicht gespeichert.
   * An seiner Stelle steht "Anschrift nicht uebernommen. Doch uebernehmen";
   * der Fokus geht auf diesen Knopf, weil der eigene mit dem Abschnitt
   * verschwindet (WCAG 2.4.3, DESIGN B9).
   */
  const dismissAddress = useCallback((): void => {
    setDismissedAnchor(addressAnchor(addressPaste));
    setAddressTouched(false);
    const recognized = recognizeWithAddress(freeText, (text) => parseFreeText(text, overridden.has('kind') ? form.kind : undefined), true);
    setAddressPaste(recognized.paste);
    setDraft(recognized.draft);
    setForm((current) => ({ ...applyFreeTextDraft(recognized.draft, current, overridden), address: undefined }));
    setErrors((current) => current.filter((error) => !error.fieldPath.startsWith('address')));
    // Dasselbe fuer das Verwerfen: ein laufendes Absenden traegt die Anschrift noch.
    forgetDuplicateCheck();
    // War die Anschrift die einzige Angabe der Organisation, klappte der Abschnitt sonst mit ihr zu
    // und nahm den Rueckweg mit (Codex-P2 #812); er bleibt offen, wie er war.
    if (orgAusSignatur) setOrgAngabenOffen(true);
    focusTarget.current = 'qc-anschrift-doch';
  }, [addressPaste, forgetDuplicateCheck, form.kind, freeText, orgAusSignatur, overridden]);

  /** "Doch uebernehmen" (B9): derselbe Vorschlag wie vor der Ablehnung, der Fokus ins erste Anschriftfeld. */
  const restoreAddress = useCallback((): void => {
    setDismissedAnchor(undefined);
    setAddressTouched(false);
    const recognized = recognizeWithAddress(freeText, (text) => parseFreeText(text, overridden.has('kind') ? form.kind : undefined), false);
    setAddressPaste(recognized.paste);
    setDraft(recognized.draft);
    setForm((current) => ({ ...applyFreeTextDraft(recognized.draft, current, overridden), address: recognized.paste.proposal }));
    forgetDuplicateCheck();
    focusTarget.current = 'qc-land';
  }, [forgetDuplicateCheck, form.kind, freeText, overridden]);

  /**
   * Der freie Text wurde geaendert (CM-CAP-001).
   *
   * Die Zerlegung laeuft bei jeder Aenderung und OHNE eigenen Schalter. Ein
   * "Übernehmen" daneben waere eine zweite Hauptaktion (NFR-UX-004) und
   * zugleich eine Huerde vor dem, was die Anforderung als selbstverstaendlich
   * beschreibt: das Feld nimmt freien Text entgegen und erzeugt daraus einen
   * Entwurf.
   */
  const changeFreeText = useCallback(
    (value: string): void => {
      setFreeText(value);
      // Die Ablehnung gilt, solange DIESELBE Anschrift im Text steht. Eine
      // andere (auch per "alles ersetzen" eingefuegt) ist ein neuer Vorschlag.
      const dismissed = dismissedAnchor !== undefined && addressAnchor(parseAddressText(value)) === dismissedAnchor;
      if (!dismissed) {
        setDismissedAnchor(undefined);
      }
      const recognized = recognizeWithAddress(value, (text) => parseFreeText(text, overridden.has('kind') ? form.kind : undefined), dismissed);
      setAddressPaste(recognized.paste);
      setDraft(recognized.draft);
      setForm((current) => ({
        ...applyFreeTextDraft(recognized.draft, current, overridden),
        address: dismissed ? undefined : addressTouched ? current.address : recognized.paste.proposal,
      }));
      setCreated(undefined);
      setCreatedAddressOf(undefined);
      setImprovement(undefined);
      setCarried(undefined);
      forgetDuplicateCheck();
    },
    [addressTouched, dismissedAnchor, forgetDuplicateCheck, form.kind, overridden],
  );

  /**
   * Der Hinweis zu genau diesem Feld, oder keiner.
   *
   * `null` und nicht `undefined`: Fluent unterscheidet "kein Hinweis" von
   * "nicht gesetzt", und `undefined` waere unter `exactOptionalPropertyTypes`
   * kein zulaessiger Wert. Mit gesetztem `validationMessage` setzt Field
   * zugleich `aria-invalid` und ein Symbol -- der Zustand haengt damit nicht an
   * der Farbe allein (ADR-0002, WCAG 1.4.1).
   */
  const errorFor = useCallback(
    (fieldPath: string): string | null => {
      const match = errors.find((error) => error.fieldPath === fieldPath);
      return match === undefined ? null : validationMessageFor(match.fieldPath, match.reasonCode).hint;
    },
    [errors],
  );

  /** Leert die Eingabe nach einer erledigten Anlage; die naechste Erfassung beginnt frisch. */
  const formularLeeren = useCallback((): void => {
    setForm(EMPTY_FORM);
    // "Arbeitet für" beginnt neu: Feld, Suche und Karte gehoeren zum verbrauchten Entwurf.
    setArbeitetFuerRunde((runde) => runde + 1);
    setFreeText('');
    setDraft(EMPTY_DRAFT);
    setFunktionWahl(undefined);
    setOverridden(new Set());
    setAddressPaste(NO_ADDRESS);
    setAddressTouched(false);
    setDismissedAnchor(undefined);
    setOrgAngabenEigen(undefined);
    setOrgAngabenOffen(undefined);
    setLeitungGewaehlt(new Set());
    // Mit dem Formular geht auch die Pruefung: der naechste Entwurf ist
    // ein anderer und wird erneut geprueft.
    forgetDuplicateCheck();
  }, [forgetDuplicateCheck]);

  /**
   * Legt die Personen der Leitung an, je mit `leadsPartyId` (S3, #786): Person und Kante
   * "leitet" entstehen zusammen oder gar nicht. Nacheinander und nicht parallel, damit die
   * Mengenbremse des Servers jede Anlage einzeln sieht. Liefert, wer fehlt.
   */
  const leitungSenden = async (
    token: string,
    namen: readonly LeitungName[],
    organisationId: string,
  ): Promise<{ readonly fehlt: readonly LeitungName[]; readonly abgelehnt: readonly LeitungName[]; readonly offen: boolean }> => {
    const fehlt: LeitungName[] = [];
    const abgelehnt: LeitungName[] = [];
    let offen = false;
    for (const name of namen) {
      const formular: QuickCaptureForm = { ...EMPTY_FORM, givenName: name.givenName, familyName: name.familyName };
      const schluessel = `${organisationId}|${name.anzeige}`;
      const anfrage = requestFor(leitungAnfragen.current.get(schluessel), {
        rumpf: captureRequestKey(buildRequestBody(formular, undefined, undefined, undefined, organisationId)),
      });
      leitungAnfragen.current.set(schluessel, anfrage);
      try {
        const ergebnis = await submitQuickCapture({ baseUrl: apiBaseUrl, accessToken: token, form: formular, leadsPartyId: organisationId, clientRequestId: anfrage.id });
        // Wie beim Kontakt: nur eine Stoerung des Dienstes behaelt die Kennung fuer den naechsten Versuch.
        const gestoert = ergebnis.kind === 'rejected' && (ergebnis.facts.status >= 500 || ergebnis.facts.status === 429);
        if (!gestoert) leitungAnfragen.current.delete(schluessel);
        // Endgueltig abgelehnt geht nie in "Erneut versuchen" mit (B15).
        if (ergebnis.kind !== 'created') (istEndgueltigAbgelehnt(ergebnis) ? abgelehnt : fehlt).push(name);
      } catch {
        // Keine Antwort: die Kennung bleibt, der zweite Versuch legt nichts doppelt an (ADR-0060).
        fehlt.push(name);
        offen = true;
      }
    }
    return { fehlt, abgelehnt, offen };
  };

  /** Haengt endgueltig Abgelehnte an die rote Leiste an; dieselbe Organisation, keine Doppelung. */
  const abgelehntMelden = (organisation: WorksForChoice, organisationNeu: boolean, namen: readonly LeitungName[]): void => {
    if (namen.length === 0) return;
    setLeitungAbgelehnt((vorher) => ({
      organisation,
      organisationNeu,
      namen: [...new Set([...(vorher?.organisation.partyId === organisation.partyId ? vorher.namen : []), ...namen.map((name) => name.anzeige)])],
    }));
  };

  /** "Erneut versuchen" am Teilerfolg der Leitung: nur die Fehlenden, nie Kontakt oder Organisation. */
  const leitungNachholen = async (): Promise<void> => {
    const rest = leitungRest;
    if (rest === undefined) return;
    setBusy(true);
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const { fehlt, abgelehnt, offen } = await leitungSenden(token, rest.namen, rest.organisation.partyId);
      setCreatedLeitung((zahl) => zahl + rest.namen.length - fehlt.length - abgelehnt.length);
      abgelehntMelden(rest.organisation, rest.organisationNeu, abgelehnt);
      setLeitungRest(fehlt.length === 0 ? undefined : { ...rest, namen: fehlt, offen });
      focusStatus.current = true;
    } finally {
      setBusy(false);
    }
  };

  const submit = useCallback(
    async (event: React.FormEvent): Promise<void> => {
      event.preventDefault();
      setBusy(true);
      setErrors([]);
      setRejection(undefined);
      setTeilerfolg(undefined);
      setCreatedOrganisation(undefined);
      setCreatedLeitung(0);
      setLeitungRest(undefined);
      setLeitungAbgelehnt(undefined);
      setCreated(undefined);
      setCreatedAddressOf(undefined);
      setImprovement(undefined);
      setCarried(undefined);
      // Die Laufnummer wird VOR dem ersten `await` gezogen. Danach ist jede
      // Aenderung am Formular ein `forgetDuplicateCheck()` und damit das Ende
      // dieses Laufs.
      const myRun = checkRun.current + 1;
      checkRun.current = myRun;
      const current = (): boolean => checkRun.current === myRun;
      // Die in dieser Erfassung angelegte Organisation, auch aus einem frueheren Versuch (Teilerfolg).
      let neuAngelegt = angelegteOrganisation;
      /** Welcher Schreibaufruf gerade laeuft; bricht seine Antwort ab, ist sein Ausgang offen. */
      let laufend: { readonly schritt: 'organisation' | 'person'; readonly name: string } | undefined;

      try {
        const token = await accessToken();
        if (!current()) {
          return;
        }
        if (token === undefined) {
          onReauthenticate();
          return;
        }

        // --- Dublette VOR der Anlage (ST-DQ-001, CM-CAP-005) ---------------
        //
        // SIE LAEUFT BEIM ABSCHICKEN UND NICHT BEI JEDEM TASTENDRUCK. Das ist
        // eine Abwaegung und keine Sparsamkeit. Ein Abgleich waehrend des
        // Tippens schickte eine halbfertige Adresse nach der anderen an den
        // Server und lieferte Namen fremder Menschen an jemanden, der noch gar
        // nichts entschieden hat (NFR-PRV-001) -- und er waere genau die
        // Unruhe, die Kapitel 5.2 Nr. 14 ausschliesst. Der Augenblick, in dem
        // die Auskunft gebraucht wird, ist der vor dem Speichern; die Story
        // verlangt woertlich "Kandidaten erscheinen vor dem endgueltigen
        // Speichern", und genau hier ist er.
        //
        // SIE LAEUFT NUR, WENN SIE ETWAS TREFFEN KANN. Ohne E-Mail und ohne
        // Rufnummer hat der exakte Abgleich nichts zu vergleichen; ein Aufruf
        // waere eine Rundreise fuer einen 422.
        //
        // UND SIE HAELT NIEMANDEN AUF. Sie verhindert die Anlage nie -- weder
        // mit Kandidaten noch mit einer Stoerung. Sie kostet EINEN zusaetzlichen
        // Druck auf denselben Knopf, und der Knopf sagt dann, was er tut.
        if (!warningSeen && isCheckable(form)) {
          const checked = await fetchDuplicateCandidates({
            baseUrl: apiBaseUrl,
            accessToken: token,
            email: form.email,
            phone: form.phone,
          }).catch(
            () =>
              // Eine Stoerung des Netzes ist keine Entwarnung. Derselbe
              // Rueckfall wie unten: Status 0, keine Antwort.
              ({
                outcome: 'ungeprueft',
                message: plainMessageFor(undefined, { status: 0, answered: false, hasCorrelationId: false }),
              }) as const,
          );
          if (!current()) {
            // DIE EINGABE HAT SICH GEAENDERT, WAEHREND WIR GEFRAGT HABEN.
            // Diese Antwort spricht ueber Werte, die nicht mehr im Formular
            // stehen; sie darf weder eine Warnung zeigen noch eine Freigabe
            // erteilen. Der Druck ist damit bewusst verbraucht: der Knopf
            // traegt wieder "Kontakt anlegen", und der naechste Druck prueft
            // die AKTUELLEN Werte vollstaendig. Mit veralteten Angaben
            // weiterzumachen waere die Alternative, und sie ist die
            // schlechtere.
            return;
          }
          if (checked.outcome === 'ungeprueft') {
            setUncheckable(checked.message);
            setWarningSeen(true);
            focusStatus.current = true;
            return;
          }
          if (checked.candidates.length > 0) {
            setCandidates(checked.candidates);
            setCandidatesHasMore(checked.hasMore);
            setWarningSeen(true);
            focusStatus.current = true;
            return;
          }
        }

        // Mit derselben Kennung, solange der Inhalt derselbe ist (ADR-0060): bricht
        // die Antwort ab, legt der naechste Druck nichts doppelt an.
        const mitKennung = async (
          gehalten: React.MutableRefObject<HeldRequest | undefined>,
          formular: QuickCaptureForm,
          wahl: WorksForChoice | undefined,
          rolle: string | undefined,
          angaben?: OrganisationAngaben,
        ): Promise<QuickCaptureResult> => {
          const anfrage = requestFor(gehalten.current, { rumpf: captureRequestKey(buildRequestBody(formular, wahl, rolle, angaben)) });
          gehalten.current = anfrage;
          const ergebnis = await submitQuickCapture({
            baseUrl: apiBaseUrl,
            accessToken: token,
            form: formular,
            worksFor: wahl,
            roleCode: rolle,
            organisationAngaben: angaben,
            clientRequestId: anfrage.id,
          });
          // Nur eine Stoerung des Dienstes kann eine Wiederholung aendern; sonst ist die naechste Sendung eine neue.
          // Ein veralteter Lauf zeigt sein Ergebnis nicht an: dann bleibt die Kennung, damit derselbe Inhalt das Original abruft.
          if (current() && !(ergebnis.kind === 'rejected' && (ergebnis.facts.status >= 500 || ergebnis.facts.status === 429))) gehalten.current = undefined;
          return ergebnis;
        };

        let wahl = worksFor;
        // Die Person schickt die Anschrift nur, wenn sie nicht mit der neuen Organisation geht (X7).
        let personFormular = form;
        // Die Anschrift der Organisation fuer die Bestaetigung (Codex-P2 #773): die Person schickt dann keine.
        let organisationsAnschriftVon: string | undefined;
        // Die neue Organisation zuerst (Vorgabe DESIGN O1-O6); scheitert sie, ist nichts angelegt.
        if (form.kind === 'person' && wahl === undefined && neueOrganisation !== undefined) {
          const name = neueOrganisation;
          laufend = { schritt: 'organisation', name };
          // Die erkannte Geschaeftsanschrift gehoert der Organisation, die Person erbt sie (X7, X3).
          const organisation = await mitKennung(organisationAnfrage, organizationForm(name, orgAusSignatur ? form.address : undefined), undefined, undefined, orgAngaben);
          if (organisation.kind === 'invalid' && organisation.errors.some((error) => isOrgAngabePfad(error.fieldPath))) {
            // Eine Angabe der Organisation steht am Feld; der Abschnitt oeffnet sich, der Fokus geht dorthin (V4).
            setErrors(organisation.errors);
            setOrgAngabenOffen(true);
            const erste = organisation.errors.find((error) => isOrgAngabePfad(error.fieldPath));
            // Eine Nummer ohne Registerart meldet sich an der Registerart (DESIGN zu #773).
            const ohneArt = erste?.fieldPath === 'commercialRegister.value' && orgAngaben.registerArt === '';
            setFokusFeld(erste === undefined ? undefined : ohneArt ? 'qc-org-register-art' : inputIdFor(erste.fieldPath));
            return;
          }
          if (organisation.kind !== 'created') {
            const superseded = organisation.kind === 'rejected' && organisation.reasonCode === CLIENT_REQUEST_SUPERSEDED;
            setRejection(
              organisation.kind === 'invalid'
                ? onlyRequestIdRejected(organisation.errors)
                  ? plainMessageFor(undefined, { status: 422, answered: true, hasCorrelationId: false })
                  : organizationInvalid(name, organisation.errors)
                : superseded
                  ? organizationRequestSuperseded(name)
                  : organisation.reasonCode === 'party_context_ambiguous'
                    ? HOUSE_AMBIGUOUS_CAPTURE_MESSAGE
                    : plainMessageFor(organisation.reasonCode, organisation.facts),
            );
            if (superseded) setNeueOrganisation(undefined);
            focusStatus.current = true;
            return;
          }
          wahl = {
            partyId: organisation.partyId,
            displayName: organisation.displayName === '' ? name : organisation.displayName,
            ...(name === organisationFromDraft(draft)?.trim() ? { fromSuggestion: name } : {}),
          };
          neuAngelegt = wahl;
          // Ab hier steht sie fest: "Arbeitet für" zeigt sie, das Kaestchen ist weg, ein zweiter Versuch legt nur die Person an.
          // Auch ihre Anschrift ist gespeichert; ein zweiter Versuch schickt sie nicht noch einmal an der Person.
          if (orgAusSignatur) {
            personFormular = { ...form, address: undefined };
            setForm((current) => ({ ...current, address: undefined }));
          }
          if (organisation.addressCreated) organisationsAnschriftVon = organisation.partyId;
          setWorksFor(wahl);
          setNeueOrganisation(undefined);
          setAngelegteOrganisation(wahl);
          if (!current()) {
            setTeilerfolg({ organisation: wahl, grund: undefined, eigenerSchritt: false });
            return;
          }
        }

        laufend = { schritt: 'person', name: '' };
        const result = await mitKennung(personAnfrage, personFormular, wahl, funktion);
        if (!current()) {
          // Die Anlage ist GELAUFEN und wird nicht zurueckgenommen -- ein
          // Datensatz existiert. Was hier unterbleibt, ist ausschliesslich das
          // Ueberschreiben eines Formulars, das die Nutzerin inzwischen weiter
          // bearbeitet: ihre Eingabe zu leeren waere Datenverlust. Der
          // Erfolgszustand entfaellt damit ebenfalls, und das ist die
          // ehrlichere Haelfte des Tauschs -- die Bestaetigung spraeche ueber
          // einen anderen Entwurf als den sichtbaren.
          return;
        }
        if (result.kind === 'created') {
          // Die Leitung nach dem Kontakt, nur an der in dieser Erfassung angelegten Organisation (S3).
          // An der vorhandenen Organisation die Neuen aus dem Abgleich (V8), nur an derselben Wahl.
          const anNeuer = gewaehlteLeitung.length > 0 && neuAngelegt !== undefined && wahl?.partyId === neuAngelegt.partyId;
          const anVorhandener = !anNeuer && gewaehlteLeitungVorhanden.length > 0 && wahl !== undefined && wahl.partyId === worksFor?.partyId;
          const leitungAn = anNeuer ? neuAngelegt : anVorhandener ? wahl : undefined;
          const leitungNamen = anNeuer ? gewaehlteLeitung : gewaehlteLeitungVorhanden;
          const leitung = leitungAn === undefined ? undefined : await leitungSenden(token, leitungNamen, leitungAn.partyId);
          // Bis zu zehn Aufrufe der Leitung: wer inzwischen weiter tippt, behaelt seine Eingabe.
          // Dieselbe Regel wie nach dem Kontakt oben (Codex-P1 #796): angelegt ist angelegt, nur das
          // Formular wird nicht ueberschrieben.
          if (leitung !== undefined && !current()) return;
          // Der Vorschlag wird VOR dem Leeren aus dem abgeschickten Formular
          // abgeleitet. Genau das ist der Grund, warum hier kein Leseweg
          // noetig ist -- auch nicht, seit es einen gibt (ST-SEA-001):
          // das 201 bestaetigt, dass gespeichert wurde, was hier steht.
          setImprovement(nextImprovementFor(form));
          // Aus demselben Grund VOR dem Leeren: was der Freitext nicht zuordnen
          // konnte, waere sonst mit dem Feld verschwunden, in dem es stand.
          // Uebernommen ist die Funktion nur, wenn sie mitging: bei einer Person mit Beziehung (Codex-P2 #744).
          // Was mit der in dieser Erfassung angelegten Organisation gespeichert ist, ist kein Rest (X6).
          const mitOrganisation = neuAngelegt !== undefined || (wahl !== undefined && angelegteOrganisation?.partyId === wahl.partyId);
          const ohneOrganisation = withoutTakenOrganisation(draft.unassigned, takenSuggestion(wahl, organisationFromDraft(draft)));
          setCarried(
            carriedOverText(
              withTakenFunction(ohneUebernommeneAngaben(ohneOrganisation, mitOrganisation ? uebernommenFuerOrganisation : leitungVorhandenErledigt), form.kind === 'person' && wahl !== undefined, funktion),
            ),
          );
          // Erst NACH dem 201 wird geleert. Ein vorher geleertes Formular
          // vernichtet die Eingabe bei jeder Ablehnung -- und die Nutzerin
          // muss alles noch einmal tippen, obwohl nur ein Feld falsch war.
          formularLeeren();
          setCreated(result.displayName);
          setCreatedAddressOf(result.addressCreated ? result.partyId : organisationsAnschriftVon);
          setCreatedAddressOrganisation(result.addressCreated || organisationsAnschriftVon === undefined ? undefined : wahl?.displayName);
          setCreatedWorksFor(form.kind === 'person' ? wahl?.displayName : undefined);
          // "Angelegt: <Person> und <Organisation>" nur, wenn sie in dieser Erfassung entstand und noch gewaehlt ist.
          setCreatedOrganisation(form.kind === 'person' && neuAngelegt !== undefined && wahl?.partyId === neuAngelegt.partyId ? neuAngelegt : undefined);
          if (leitungAn !== undefined && leitung !== undefined) {
            setCreatedLeitung(leitungNamen.length - leitung.fehlt.length - leitung.abgelehnt.length);
            abgelehntMelden(leitungAn, anNeuer, leitung.abgelehnt);
            if (leitung.fehlt.length > 0) setLeitungRest({ organisation: leitungAn, organisationNeu: anNeuer, namen: leitung.fehlt, offen: leitung.offen });
          }
          setAngelegteOrganisation(undefined);
          setWorksFor(undefined);
          // Der Knopf war waehrend des Laufs gesperrt; ohne Ziel laege der Fokus auf BODY.
          focusStatus.current = true;
          return;
        }
        if (result.kind === 'rejected' && result.reasonCode === CLIENT_REQUEST_SUPERSEDED) {
          // Angekommen und inzwischen geaendert (ADR-0060 Ziffer 9): die Eingabe ist erledigt.
          formularLeeren();
          setAngelegteOrganisation(undefined);
          setWorksFor(undefined);
          setRejection(CAPTURE_REQUEST_SUPERSEDED);
          focusStatus.current = true;
          return;
        }
        if (result.kind === 'invalid' && !onlyRequestIdRejected(result.errors)) {
          setErrors(result.errors);
          if (result.errors.some((error) => error.fieldPath === 'worksForPartyId')) {
            // Die Wahl taugt nicht: sie faellt weg, die Suche steht wieder offen
            // und holt den Fokus (quick-capture-arbeitet-fuer.tsx).
            setWorksFor(undefined);
          }
          if (neuAngelegt !== undefined) setTeilerfolg({ organisation: neuAngelegt, grund: undefined, eigenerSchritt: false, markiert: result.errors.length });
          // NFR-ACC-001: der Fokus wandert auf das erste beanstandete Feld.
          // Genau dafuer traegt die 422-Antwort ueberhaupt Feldpfade -- die
          // Ausnahme von der Security-Baseline in ADR-0019 Nachtrag Punkt 4
          // waere sonst bezahlt und nicht eingeloest.
          const first = result.errors[0];
          const inputId = first === undefined ? undefined : inputIdFor(first.fieldPath);
          if (inputId !== undefined) {
            document.getElementById(inputId)?.focus();
          } else if (neuAngelegt !== undefined) {
            focusStatus.current = true;
          }
          return;
        }
        const reasonCode = result.kind === 'rejected' ? result.reasonCode : undefined;
        const facts = result.kind === 'rejected' ? result.facts : { status: 422, answered: true, hasCorrelationId: false };
        const linkRejection = reasonCode === undefined ? undefined : WORKS_FOR_REJECTIONS[reasonCode];
        if (linkRejection !== undefined) {
          // Die Organisation taugt nicht (mehr) oder die Bremse greift: die
          // Wahl faellt weg, das Getippte bleibt stehen (NFR-UX-005).
          setWorksFor(undefined);
        }
        const houseRejection = reasonCode === 'party_context_ambiguous' ? HOUSE_AMBIGUOUS_CAPTURE_MESSAGE : undefined;
        const reused = reasonCode === 'client_request_reused' ? CAPTURE_REQUEST_REUSED : undefined;
        const grund = linkRejection ?? houseRejection ?? reused ?? plainMessageFor(reasonCode, facts);
        if (neuAngelegt !== undefined) {
          setTeilerfolg({ organisation: neuAngelegt, grund, eigenerSchritt: linkRejection !== undefined });
          focusStatus.current = true;
          return;
        }
        setRejection(grund);
      } catch (error) {
        // Ein gescheiterter Aufruf ist eine Stoerung und KEINE Ablehnung. Die
        // Unterscheidung ist dieselbe, die am 24.08.2026 eine CORS-Blockade
        // eine Stunde lang als Anmeldefehler hat erscheinen lassen.
        // Status 0: es kam gar keine Antwort. Der Rueckfall nennt dann als
        // einzigen Fall eine Handlung, die die Nutzerin selbst ausfuehren kann.
        // Die Kennung bleibt gehalten: der naechste Druck mit demselben Inhalt legt nichts doppelt an.
        if (laufend === undefined) setRejection(NICHT_ERREICHBAR_VOR_ANLAGE);
        else if (laufend.schritt === 'organisation') setRejection(organizationUnreachable(laufend.name));
        else if (neuAngelegt === undefined) setRejection(CAPTURE_UNREACHABLE);
        else setTeilerfolg({ organisation: neuAngelegt, grund: undefined, eigenerSchritt: false, offen: true });
        focusStatus.current = true;
      } finally {
        setBusy(false);
      }
    },
    [accessToken, angelegteOrganisation, apiBaseUrl, draft, erkannteOrgAngaben, forgetDuplicateCheck, form, formularLeeren, funktion, gewaehlteLeitung, gewaehlteLeitungVorhanden, leitungSenden, leitungVorhandenErledigt, neueOrganisation, onReauthenticate, orgAngaben, uebernommenFuerOrganisation, warningSeen, worksFor],
  );

  // Was aus dem Freitext wirklich nicht uebernommen ist: ohne den Organisationsnamen, wenn "Arbeitet für" ihn haelt.
  // Ebenso ohne die Funktion, wenn sie als Code an "Arbeitet für" mitgeht (S1b).
  // Bei "Neu" haelt die Karte den Namen, und die Angaben der Organisation stehen in ihren Feldern (X6).
  const haltendeWahl: WorksForChoice | undefined = worksFor ?? (orgNeu && neueOrganisation !== undefined ? { partyId: '', displayName: neueOrganisation } : undefined);
  const ohneOrganisation = withoutTakenOrganisation(draft.unassigned, takenSuggestion(haltendeWahl, organisationFromDraft(draft)));
  // Mit dieser Erfassung angelegt: ihre Angaben sind gespeichert. Sonst gewaehlt und vorhanden: dort pflegen (§1).
  const angabenGespeichert = orgNeu || (worksFor !== undefined && angelegteOrganisation?.partyId === worksFor.partyId);
  const offeneBruchstuecke = withTakenFunction(
    angabenGespeichert
      ? ohneUebernommeneAngaben(ohneOrganisation, uebernommenFuerOrganisation)
      : worksFor !== undefined && form.kind === 'person' && takenSuggestion(worksFor, organisationFromDraft(draft)) !== undefined
        ? // Nur die Organisation der Signatur ist gemeint; bei einer anderen Wahl bleibt der allgemeine Grund.
          ohneUebernommeneAngaben(alsAngabenDerOrganisation(ohneOrganisation, erkannteOrgAngabenAlle.taken), leitungVorhandenTaken)
        : ohneOrganisation,
    vorschauOrganisation !== undefined,
    funktion,
  );
  // Die Zahl in der Zusammenfassung zaehlt denselben Rest wie "Nicht übernommen" (X6).
  const zusammenfassungsEntwurf = { ...draft, unassigned: offeneBruchstuecke };

  const hatEingaben =
    freeText.trim() !== '' ||
    [form.givenName, form.familyName, form.legalName, form.email, form.phone].some((wert) => wert.trim() !== '') ||
    form.address !== undefined ||
    worksFor?.partyId !== anfangsOrganisation?.partyId;

  // Dieselben Felder fuer die Anschrift der Person und, bei "Neu", der Organisation (X7).
  const anschriftFelder =
    form.address === undefined ? undefined : (
      <>
        <Field
          label="Land"
          required
          hint={form.address.countryCode === '' ? (addressFieldHint(addressPaste, 'countryCode') ?? null) : null}
          validationMessage={errorFor('address.countryCode')}
        >
          <Select
            className={anlegenStyles.auswahl}
            id="qc-land"
            value={form.address.countryCode}
            onChange={(_event, data) => updateAddress({ countryCode: data.value })}
          >
            {form.address.countryCode === '' ? <option value="">{ADDRESS_CHOOSE_COUNTRY}</option> : undefined}
            {COUNTRIES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Straße und Hausnummer"
          required
          hint={form.address.addressLine1.trim() === '' ? (addressFieldHint(addressPaste, 'addressLine1') ?? null) : null}
          validationMessage={errorFor('address.addressLine1')}
        >
          <Input
            id="qc-zeile1"
            autoComplete="address-line1"
            value={form.address.addressLine1}
            onChange={(_event, data) => updateAddress({ addressLine1: data.value })}
          />
        </Field>
        <Field
          label="Adresszusatz"
          hint="Zum Beispiel Abteilung, Hinterhaus oder c/o. Kann leer bleiben."
          validationMessage={errorFor('address.addressLine2')}
        >
          <Input
            id="qc-zeile2"
            autoComplete="address-line2"
            value={form.address.addressLine2}
            onChange={(_event, data) => updateAddress({ addressLine2: data.value })}
          />
        </Field>
        <div className={mergeClasses(styles.formRow, anlegenStyles.zeile)}>
          <Field
            label={form.address.countryCode === POSTAL_CODE_REQUIRED_COUNTRY ? 'Postleitzahl' : 'Postleitzahl (falls vorhanden)'}
            required={form.address.countryCode === POSTAL_CODE_REQUIRED_COUNTRY}
            validationMessage={errorFor('address.postalCode')}
          >
            <Input
              id="qc-plz"
              autoComplete="postal-code"
              value={form.address.postalCode}
              onChange={(_event, data) => updateAddress({ postalCode: data.value })}
            />
          </Field>
          <Field label="Ort" required validationMessage={errorFor('address.locality')}>
            <Input
              id="qc-ort"
              autoComplete="address-level2"
              value={form.address.locality}
              onChange={(_event, data) => updateAddress({ locality: data.value })}
            />
          </Field>
        </div>
        <div>
          {/* Zurueckhaltend und KEINE zweite Hauptaktion: sie nimmt nur
              den Vorschlag zurueck. */}
          <Button appearance="subtle" type="button" onClick={dismissAddress}>
            {ADDRESS_DISMISS_LABEL}
          </Button>
        </div>
      </>
    );

  // Abgelehnt, solange dieselbe Anschrift im Freitext steht: an ihrer Stelle der Rueckweg (B9).
  const anschriftAbgelehnt =
    form.address !== undefined || !addressDismissed ? undefined : (
      <div className={anlegenStyles.abgelehnt}>
        <Body1>{ADDRESS_DISMISSED}</Body1>
        <Button appearance="subtle" type="button" id="qc-anschrift-doch" onClick={restoreAddress}>
          {ADDRESS_RESTORE_LABEL}
        </Button>
      </div>
    );

  return (
    <Card className={mergeClasses(styles.card, anlegenStyles.karte)}>
      {/* KEIN eigener Kartenkopf. Der Rahmen setzt bereits <h1>Kontakt
          anlegen</h1> aus der Bildschirmtabelle; ein <h2> mit demselben Wort
          direkt darunter stand am 26.08.2026 zweimal untereinander auf dem
          Bildschirm. Der Strukturnachweis konnte das nicht sehen: er prueft
          "genau eine erste Ueberschrift", und die war korrekt. Eine Karte, die
          den einzigen Inhalt eines Bildschirms traegt, braucht keine zweite
          Beschriftung -- die Ueberschrift des Bildschirms ist ihre. */}
      {/* Ein Satz (v4); wo der Kontakt sichtbar wird, steht klein unter der Hauptaktion. */}
      <Body1>Ein Name genügt. Neue Kontakte starten als Entwurf; den Rest ergänzt du später.</Body1>

      {/* Die Statusregion steht VOR dem Formular und traegt aria-live: wer mit
          einem Screenreader arbeitet, erfaehrt das Ergebnis, ohne es zu
          suchen. Die Meldung nennt den Zustand auch in Worten, nicht nur ueber
          Farbe und Symbol (ADR-0002, WCAG 1.4.1). */}
      <div aria-live="polite" role="status" id={STATUS_REGION_ID} tabIndex={-1}>
        {created !== undefined ? (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>
                {createdOrganisation === undefined
                  ? createdLeitung > 0 && createdWorksFor !== undefined
                    ? leitungAngelegtVorhandenTitel(created, createdWorksFor, createdLeitung)
                    : `Angelegt: ${created}`
                  : createdLeitung > 0
                    ? leitungAngelegtTitel(created, createdOrganisation.displayName, createdLeitung)
                    : createdBothTitle(created, createdOrganisation.displayName)}
              </MessageBarTitle>
              <div>
                {createdOrganisation === undefined
                  ? createdLeitung > 0
                    ? alleSichtbarZeile(houseName, houseArt)
                    : `Der Kontakt ist als Entwurf gespeichert. ${capturedVisibilityLine(houseName, houseArt)}`
                  : createdLeitung > 0
                    ? alleSichtbarZeile(houseName, houseArt)
                    : bothSavedLine(houseName, houseArt)}
              </div>
              {/* Die Anschrift ist ein EIGENER Datensatz mit eigenem Zweck und
                  eigener Pflege -- das sagt die Bestaetigung ausdruecklich,
                  mit dem Weg dorthin (CM-UX-004: Wirkung und Rueckweg). */}
              {createdOrganisation !== undefined ? (
                // Der Name steht schon im Titel; die Zeile ist nur der Weg dorthin, ein Link, ein Tabstopp.
                <div>
                  <AppLink to={partyHref(createdOrganisation.partyId)}>
                    {/* Vorgelesen mit Namen; als eine Spanne, sonst setzte der Browser ein Leerzeichen vor den Doppelpunkt. */}
                    <span aria-hidden="true">Organisation öffnen</span>
                    <span className={anlegenStyles.verborgen}>{`Organisation öffnen: ${createdOrganisation.displayName}`}</span>
                  </AppLink>
                </div>
              ) : createdWorksFor === undefined ? undefined : (
                <div>{worksForSaved(createdWorksFor)}</div>
              )}
              {createdAddressOf === undefined ? undefined : (
                <div>
                  {ADDRESS_SAVED} <AppLink to={addressesHref(createdAddressOf)}>
                    {createdAddressOrganisation === undefined ? ADDRESS_SAVED_LINK : `Anschrift von ${createdAddressOrganisation}`}
                  </AppLink>
                </div>
              )}
              {/* Die naechste sinnvolle Verbesserung DIESES Entwurfs -- oder,
                  wenn nichts Sinnvolles fehlt, die naechste Aufgabe. Genau
                  eines von beiden und nie beides: zwei naechste Schritte
                  nebeneinander sind zwei Hauptaktionen (NFR-UX-004), und die
                  Nutzerin muesste raten, welcher gemeint ist.

                  KEIN EIGENER SCHALTER dafuer. Der Bildschirm hat genau eine
                  Hauptaktion, und "Kontakt anlegen" ist sie. Ein zweiter
                  Schalter, der einen Bearbeitungsflow oeffnet, waere zudem eine
                  Zusage auf einen Weg, den erst ST-PTY-002 baut -- ein Knopf
                  ins Nichts ist schlechter als ein Satz, der stimmt. */}
              {/* Was der Freitext nicht zuordnen konnte. KEIN Vorschlag und
                  keine Handlung, sondern eine Auskunft darueber, was gerade
                  gespeichert wurde und was nicht -- die zweite Haelfte von
                  "unbekannte Angaben verhindern die Anlage nicht"
                  (CM-CAP-001, Kapitel 5.2 Nr. 3 und Nr. 5).

                  SIE STEHT VOR DEM VORSCHLAG, und das ist eine Korrektur aus
                  dem Experience-Review vom 29.08.2026. Umgekehrt las sich die
                  Bestaetigung in einem sehr gewoehnlichen Fall widerspruechlich:
                  ein vollstaendiger Entwurf mit einer Notiz im Freitext ergab
                  "Es fehlt nichts Wesentliches." unmittelbar gefolgt von
                  "Nicht mitgespeichert: ...". Beide Saetze stimmen und sprechen
                  ueber Verschiedenes -- der eine ueber die Felder des Kontakts,
                  der andere ueber den eingefuegten Text. Nebeneinander gelesen
                  widersprechen sie sich trotzdem. Die Auskunft ueber das eben
                  Gespeicherte gehoert deshalb neben das eben Gespeicherte, und
                  der Blick nach vorn bleibt das Letzte, was dasteht. */}
              {carried === undefined ? undefined : <div>{carried}</div>}
              {improvement === undefined ? (
                <div>{NOTHING_TO_IMPROVE}</div>
              ) : (
                <>
                  <div>
                    {NEXT_IMPROVEMENT_LABEL}: {improvement.gap}
                  </div>
                  <div>{improvement.benefit}</div>
                </>
              )}
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {/* DIE DUBLETTENWARNUNG (ST-DQ-001, CM-CAP-005).

            SIE STEHT IN DIESER REGION UND BEKOMMT KEINE EIGENE. Sie ist das
            Ergebnis der Handlung "Kontakt anlegen" wie die Bestaetigung und
            die Ablehnung -- eine zweite Region mit `role="status"` machte
            "die Statusmeldung" mehrdeutig (NFR-ACC-001).

            `intent="warning"` UND NICHT `error`: nichts ist schiefgegangen.
            Der Zustand steht ausserdem in WORTEN und nicht allein in der Farbe
            (WCAG 1.4.1, ADR-0002).

            KEIN SCORE, KEINE PROZENTZAHL, KEIN "wahrscheinlich dieselbe
            Person". Es gibt keine Messgrundlage (CM-CAP-006), und eine Zahl
            ohne Messgrundlage ist eine Behauptung mit Nachkommastelle. */}
        {candidates.length > 0 ? (
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>{DUPLICATE_HEADING}</MessageBarTitle>
              <div>{duplicateSummary(candidates.length, candidatesHasMore)}</div>
              <ul className={freeTextStyles.candidates}>
                {candidates.map((candidate) => (
                  <li key={candidate.partyId} className={freeTextStyles.candidate}>
                    {/* EIN ECHTES `<a href>` und keine Schaltflaeche: "in neuem
                        Tab oeffnen" und "Adresse kopieren" muessen
                        funktionieren, und ein Verweis ist genau das.

                        `target="_blank"` mit SICHTBAREM Hinweis im Namen: der
                        Bildschirm hat keinen Entwurfsschutz, und ein Verweis in
                        derselben Seite vernichtete das Getippte in dem Moment,
                        in dem die Nutzerin dem Rat der Anwendung folgt
                        (Begruendung in duplicate-candidates.ts). */}
                    <a
                      href={partyHref(candidate.partyId)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {candidateLinkLabel(candidate.displayName)}
                    </a>
                    {/* Die unterscheidenden Merkmale -- und nur sie. Art,
                        Zustand und WORAN der Treffer liegt. Kein Kontaktweg:
                        die Nutzerin hat den Wert selbst getippt, und die
                        uebrigen gehen sie an dieser Stelle nichts an. */}
                    <Caption1 as="p">
                      {kindLabel(candidate.kind)} · {lifecycleLabel(candidate.lifecycleStatus)} ·{' '}
                      {matchReason(candidate)}
                    </Caption1>
                  </li>
                ))}
              </ul>
              <div>{DUPLICATE_IS_NOT_A_PROOF}</div>
              <div>{DUPLICATE_IS_NOT_A_BLOCKER}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {/* Die Pruefung hat nicht stattgefunden. Sie wird BENANNT und nicht
            verschwiegen: eine unterbliebene Pruefung als Entwarnung zu
            behandeln waere die gefaehrlichste Auskunft dieses Bildschirms
            (NFR-UX-005). Die Anlage bleibt trotzdem moeglich -- eine gestoerte
            Nebenpruefung darf die Hauptaufgabe nicht anhalten. */}
        {uncheckable !== undefined ? (
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>Die Prüfung auf vorhandene Kontakte war nicht möglich.</MessageBarTitle>
              <div>{uncheckable.explanation}</div>
              <div>
                Du kannst den Kontakt trotzdem anlegen. Ob es ihn schon gibt, ist dabei ungeprüft geblieben.
              </div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {/* Im Teilerfolg sagt die eine Warnleiste es; eine zweite, rote Leiste stellte das Gespeicherte nach hinten. */}
        {errors.length > 0 && teilerfolg === undefined && orgNeu && errors.some((error) => isOrgAngabePfad(error.fieldPath)) ? (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>{organisationAngabenAbgelehnt(errors.length)}</MessageBarTitle>
            </MessageBarBody>
          </MessageBar>
        ) : errors.length > 0 && teilerfolg === undefined ? (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>{validationSummary(errors.length)}</MessageBarTitle>
              <div>Korrigiere die markierten Felder und lege den Kontakt erneut an.</div>
              {errors.some((error) => error.fieldPath.startsWith('address')) ? <div>{ADDRESS_ERRORS_NEXT_STEP}</div> : undefined}
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {teilerfolg === undefined ? undefined : (
          // warning und nicht error: gespeichert wurde etwas, die Organisation (O4).
          <TeilerfolgLeiste
            meldung={
              teilerfolg.offen === true
                ? partialUncertainMessage(teilerfolg.organisation.displayName)
                : partialCaptureMessage(teilerfolg.organisation.displayName, teilerfolg.grund, !teilerfolg.eigenerSchritt, teilerfolg.markiert)
            }
          />
        )}
        {leitungAbgelehnt === undefined
          ? undefined
          : (() => {
              // Rot und ohne Knopf (B15): ein zweiter Versuch aenderte nichts.
              const meldung = leitungEndgueltigAbgelehnt(leitungAbgelehnt.namen, leitungAbgelehnt.organisation.displayName, leitungAbgelehnt.organisationNeu);
              return (
                <MessageBar intent="error" layout="multiline">
                  <MessageBarBody>
                    <MessageBarTitle>{meldung.headline}</MessageBarTitle>
                    <div>{meldung.explanation}</div>
                    <div>{meldung.nextStep}</div>
                  </MessageBarBody>
                </MessageBar>
              );
            })()}
        {leitungRest === undefined
          ? undefined
          : (() => {
              // warning: der Kontakt (und eine neue Organisation) ist gespeichert, nur ein Teil der Leitung fehlt (S3).
              const meldung = leitungTeilerfolg(
                leitungRest.namen.map((name) => name.anzeige),
                leitungRest.organisation.displayName,
                leitungRest.offen,
                leitungRest.organisationNeu,
              );
              return (
                <MessageBar intent="warning" layout="multiline">
                  <MessageBarBody>
                    <MessageBarTitle>{meldung.headline}</MessageBarTitle>
                    <div>{meldung.explanation}</div>
                    <div>{meldung.nextStep}</div>
                  </MessageBarBody>
                  <MessageBarActions>
                    <Button disabled={busy} onClick={() => void leitungNachholen()}>
                      Erneut versuchen
                    </Button>
                  </MessageBarActions>
                </MessageBar>
              );
            })()}
        {rejection !== undefined ? (
          // Ein Fehler, wo nichts gespeichert sein kann; eine Warnung, wo offen ist, was gilt.
          <MessageBar intent={rejection === NICHT_ERREICHBAR_VOR_ANLAGE ? 'error' : 'warning'}>
            <MessageBarBody>
              <MessageBarTitle>{rejection.headline}</MessageBarTitle>
              <div>{rejection.explanation}</div>
              <div>{rejection.nextStep}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
      </div>
      <VorgangNach meldungen={[rejection, teilerfolg?.grund]} />

      <form className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        {/* DAS UNIVERSELLE FELD AUS CM-CAP-001, und es steht bewusst ZUERST.
            Die Anforderung nennt "Name, E-Mail, Telefon, Firma oder freien
            Text" in einem Atemzug; ein Freitextfeld unterhalb der Einzelfelder
            waere ein Nachtrag statt eines Angebots. Es ist trotzdem kein
            Pflichtfeld und kein Modus: wer lieber tippt, tippt unten weiter --
            "kein Feld ohne unmittelbaren Zweck ist initial Pflicht"
            (CM-CAP-002). */}
        <Field label={FREE_TEXT_LABEL} hint={FREE_TEXT_HINT}>
          <Textarea
            id="qc-freitext"
            className={freeTextStyles.area}
            value={freeText}
            placeholder={FREE_TEXT_PLACEHOLDER}
            rows={4}
            resize="vertical"
            onChange={(_event, data) => changeFreeText(data.value)}
          />
        </Field>

        {/* WAS ERKANNT WURDE -- vor der Speicherung und in Worten.
            CM-CAP-001 verlangt woertlich, dass erkannte Felder UND der
            Party-Typ vor dem Speichern gezeigt werden. Die WERTE stehen nicht
            hier, sondern in den Feldern darunter: dort sind sie zugleich
            korrigierbar, und eine zweite Anzeige derselben Werte waere eine
            zweite Wahrheit auf demselben Bildschirm (NFR-UX-004).

            Die Vorlesestimme bekommt nur die Zusammenfassung, nicht die Liste
            -- die Begruendung steht bei `recognitionSummary`. `aria-live` OHNE
            `role="status"`: die Statusregion ueber dem Formular ist die eine
            Stelle fuer das ERGEBNIS einer Anlage, und eine zweite Region
            desselben Rollennamens machte "die Statusmeldung" mehrdeutig --
            fuer Hilfsmittel wie fuer die Proben, die sie halten. */}
        {freeText.trim().length > 0 ? (
          // Eine Zeile statt eines Blocks mit Zwischentiteln (DESIGN zu #548, Michael D-07): was
          // erkannt wurde, steht hier; die Werte stehen nur in den Feldern darunter, wo sie
          // korrigierbar sind. Was nicht uebernommen wurde, nennt die Disclosure mit Grund.
          <section className={freeTextStyles.recognition} aria-labelledby="qc-erkennung">
            <h2 className={anlegenStyles.verborgen} id="qc-erkennung">
              {RECOGNITION_HEADING}
            </h2>
            <div aria-live="polite">
              {orgNeu && orgAngabenZahl > 0
                ? recognitionSummaryWithAddress(recognitionSummary(zusammenfassungsEntwurf), draft, NOTHING_RECOGNIZED, organisationAngabenGefunden(orgAngabenZahl))
                : addressPaste.proposal === undefined || form.address === undefined
                  ? recognitionSummary(zusammenfassungsEntwurf)
                  : recognitionSummaryWithAddress(recognitionSummary(zusammenfassungsEntwurf), draft, NOTHING_RECOGNIZED)}
            </div>
            {offeneBruchstuecke.length > 0 ? (
              <details className={anlegenStyles.details}>
                <summary>{unassignedLine(offeneBruchstuecke)}</summary>
                <ul className={freeTextStyles.list}>
                  {offeneBruchstuecke.map((fragment: UnassignedFragment, index: number) => (
                    <li key={`${fragment.reason}-${index}`} className={freeTextStyles.fragment}>
                      „{fragment.text}“ —{' '}
                      {fragment.reason === 'belongs_to_organisation' && worksFor !== undefined
                        ? organisationsGrund(worksFor.displayName)
                        : UNASSIGNED_REASON_TEXTS[fragment.reason]}
                    </li>
                  ))}
                </ul>
                <div>{UNASSIGNED_IS_NOT_A_BLOCKER}</div>
              </details>
            ) : undefined}
          </section>
        ) : undefined}

        {/* Segment (v4 .seg) aus zwei Umschaltknoepfen: zwei gleichwertige Arten, genau eine gilt,
            und sie baut das Formular darunter um; "Firma" hiess fuer Verlage zu eng. */}
        {/* Ein Feldfehler zur Art fokussiert diesen Rahmen; er gibt den Fokus an die gewaehlte Art
            weiter, damit kein namenloses Element den Fokus haelt. */}
        <div
          className={anlegenStyles.art}
          id="qc-art"
          tabIndex={-1}
          onFocus={(event) => {
            if (event.target === event.currentTarget) (event.currentTarget.querySelector<HTMLButtonElement>('[aria-pressed="true"]') ?? event.currentTarget.querySelector<HTMLButtonElement>('[aria-pressed]'))?.focus();
          }}
        >
          <Segment label="Art des Kontakts" optionen={ART_OPTIONEN} gewaehlt={form.kind} onWahl={(id) => update({ kind: id as PartyKind })} />
          {errorFor('kind') === null ? undefined : (
            <Caption1 className={anlegenStyles.artFehler} role="alert">
              {errorFor('kind')}
            </Caption1>
          )}
        </div>

        {/* Progressive Offenlegung (NFR-UX-004): sichtbar ist nur, was zur
            gewaehlten Art gehoert. Beide Bloecke gleichzeitig zu zeigen waere
            die Megamaske, die ALT-BAS-009 ausdruecklich ablehnt. */}
        {form.kind === 'person' ? (
          <div className={mergeClasses(styles.formRow, anlegenStyles.zeile)}>
            <Field label="Vorname" validationMessage={errorFor('person.givenName') ?? errorFor('person')}>
              <Input
                id="qc-vorname"
                value={form.givenName}
                autoComplete="given-name"
                onChange={(_event, data) => update({ givenName: data.value })}
              />
            </Field>
            <Field label="Nachname" validationMessage={errorFor('person.familyName')}>
              <Input
                id="qc-nachname"
                value={form.familyName}
                autoComplete="family-name"
                onChange={(_event, data) => update({ familyName: data.value })}
              />
            </Field>
          </div>
        ) : (
          <Field
            label="Name der Organisation"
            validationMessage={errorFor('organization.legalName') ?? errorFor('organization')}
            required
          >
            <Input
              id="qc-firmenname"
              value={form.legalName}
              autoComplete="organization"
              onChange={(_event, data) => update({ legalName: data.value })}
            />
          </Field>
        )}

        <div className={mergeClasses(styles.formRow, anlegenStyles.zeile)}>
          <Field label="E-Mail" validationMessage={errorFor('email')}>
            <Input
              id="qc-email"
              type="email"
              value={form.email}
              autoComplete="email"
              onChange={(_event, data) => update({ email: data.value })}
            />
          </Field>
          <Field label="Telefon" validationMessage={errorFor('phone')}>
            <Input
              id="qc-telefon"
              type="tel"
              value={form.phone}
              autoComplete="tel"
              onChange={(_event, data) => update({ phone: data.value })}
            />
          </Field>
        </div>

        {/* DIE GESCHAEFTSANSCHRIFT ("Anschrift einfuegen", 29.09.2026).

            NUR, WENN DER FREITEXT EINE ENTHIELT -- kein leeres
            Anschriftformular in der Anlage (progressive Offenlegung,
            NFR-UX-004); gepflegt werden Anschriften unter "Anschriften".
            VORBEFUELLT ZUM BESTAETIGEN: die Felder sind der Vorschlag und
            zugleich die Korrektur, und bestaetigt wird mit der EINEN
            Hauptaktion "Kontakt anlegen" wie bei jedem anderen erkannten Feld.
            Der Satz darunter sagt vorher, was dabei entsteht: ein eigener
            Datensatz, Zweck Geschaeftsanschrift, nicht geprueft. Hinweise des
            Zerlegers stehen AM FELD, an dem sie zu handeln sind. */}
        {form.address !== undefined && !orgAusSignatur ? (
          <section className={freeTextStyles.recognition} aria-labelledby="qc-anschrift">
            <h2 className={freeTextStyles.heading} id="qc-anschrift">
              {ADDRESS_SECTION_HEADING}
            </h2>
            <Caption1>{ADDRESS_SECTION_EXPLANATION}</Caption1>
            {anschriftFelder}
          </section>
        ) : orgAusSignatur ? undefined : (
          anschriftAbgelehnt
        )}

        {/* "ARBEITET FÜR" (ST-REL-001): nach den Kontaktwegen und einer
            erkannten Anschrift, unmittelbar vor der Hauptaktion -- zugeklappt,
            weil die meisten Anlagen ohne sie auskommen (NFR-UX-004). Nur bei
            einer Person: works_for verbindet Person und Organisation. */}
        {form.kind === 'person' ? (
          <ArbeitetFuer
            key={arbeitetFuerRunde}
            apiBaseUrl={apiBaseUrl}
            accessToken={accessToken}
            onReauthenticate={onReauthenticate}
            value={worksFor}
            onChange={setWorksFor}
            error={errorFor('worksForPartyId')}
            disabled={busy}
            vorschlag={organisationFromDraft(draft)}
            neu={neueOrganisation}
            onNeuChange={setNeueOrganisation}
            angabenNeu={
              <OrganisationAngabenAbschnitt
                angaben={orgAngaben}
                onChange={(patch) => setOrgAngabenEigen((eigen) => ({ ...(eigen ?? erkannteOrgAngaben.angaben), ...patch }))}
                erkannt={orgAngabenZahl}
                offen={angabenOffen}
                onOffenChange={setOrgAngabenOffen}
                fehlerAm={errorFor}
                anschrift={orgAusSignatur ? anschriftFelder : undefined}
                anschriftAbgelehnt={orgAusSignatur ? anschriftAbgelehnt : undefined}
                disabled={busy}
              />
            }
            leitungVorhanden={
              worksFor !== undefined && abgleichZustand !== undefined && abgleichZustand !== 'keiner' ? (
                <LeitungAbgleichGruppe
                  organisation={worksFor.displayName}
                  organisationId={worksFor.partyId}
                  zustand={abgleichZustand}
                  gewaehlt={leitungGewaehlt}
                  disabled={busy}
                  onChange={(anzeige, an) =>
                    setLeitungGewaehlt((vorher) => {
                      const neu = new Set(vorher);
                      if (an) neu.add(anzeige);
                      else neu.delete(anzeige);
                      return neu;
                    })
                  }
                />
              ) : erkannteLeitung.namen.length === 0 || !orgDieserErfassung || worksFor === undefined ? undefined : (
                <LeitungGruppe
                  organisation={worksFor.displayName}
                  namen={erkannteLeitung.namen}
                  gewaehlt={leitungGewaehlt}
                  disabled={busy}
                  onChange={(anzeige, an) =>
                    setLeitungGewaehlt((vorher) => {
                      const neu = new Set(vorher);
                      if (an) neu.add(anzeige);
                      else neu.delete(anzeige);
                      return neu;
                    })
                  }
                />
              )
            }
            leitungNeu={
              erkannteLeitung.namen.length === 0 || neueOrganisation === undefined ? undefined : (
                <LeitungGruppe
                  organisation={neueOrganisation}
                  namen={erkannteLeitung.namen}
                  gewaehlt={leitungGewaehlt}
                  disabled={busy}
                  onChange={(anzeige, an) =>
                    setLeitungGewaehlt((vorher) => {
                      const neu = new Set(vorher);
                      if (an) neu.add(anzeige);
                      else neu.delete(anzeige);
                      return neu;
                    })
                  }
                />
              )
            }
          />
        ) : undefined}

        {/* Die Funktion an "Arbeitet für" (S1b), erst mit einer Organisation (progressive
            Offenlegung) und freiwillig. Derselbe Satz wie im Beziehungsabschnitt; ein Vorschlag aus
            der Signatur nennt seinen Wortlaut, bestaetigt wird mit der einen Hauptaktion. */}
        {vorschauOrganisation === undefined ? undefined : (
          <Field
            label="Funktion (freiwillig)"
            hint={
              erkannteFunktion !== undefined && funktion !== undefined && funktion === vorgeschlageneFunktion
                ? `Vorgeschlagen aus „${erkannteFunktion}“. Wofür der Kontakt dort zuständig ist, zum Beispiel Presse oder Einkauf.`
                : 'Wofür der Kontakt dort zuständig ist, zum Beispiel Presse oder Einkauf.'
            }
            validationMessage={errorFor('roleCode')}
            validationState={errorFor('roleCode') === null ? 'none' : 'error'}
          >
            <Select
              className={anlegenStyles.auswahl}
              id="qc-funktion"
              disabled={busy}
              value={funktion ?? ''}
              onChange={(_event, data) => setFunktionWahl({ fuer: erkannteFunktion, code: data.value === '' ? undefined : data.value })}
            >
              <option value="">Keine Funktion</option>
              {ROLE_CODES.map((code) => (
                <option key={code} value={code}>
                  {roleLabel(code)}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {/* "Das wird angelegt": nur wenn eine Organisation verknuepft oder mitangelegt wird, direkt
            ueber dem Knopf, den sie erklaert. */}
        {vorschauOrganisation === undefined ? undefined : (
          <AnlegenVorschau
            name={`${form.givenName} ${form.familyName}`}
            ersterKontaktweg={form.email.trim() !== '' ? form.email : form.phone}
            funktion={funktionGilt === undefined ? undefined : roleLabel(funktionGilt)}
            organisation={vorschauOrganisation}
            angaben={orgNeu ? organisationVorschauZeilen(orgAngaben, orgAusSignatur ? form.address : undefined) : []}
            leitung={(leitungFuerNeue ? gewaehlteLeitung : gewaehlteLeitungVorhanden).map((name) => name.anzeige)}
          />
        )}

        <div className={anlegenStyles.fuss} ref={fussRef} data-anlegen-fuss="">
          {/* Abbrechen fuehrt zurueck zu den Kontakten, ohne zu speichern; zurueckhaltend, damit
              "Anlegen" die einzige betonte Aktion bleibt. */}
          <NebenKnopf
            type="button"
            disabled={busy}
            ref={(knoten: HTMLButtonElement | null) => {
              abbrechenRef.current = knoten;
            }}
            onClick={() => {
              // Ohne Eingaben geht es sofort zurueck; mit Eingaben fragt ein Dialog nach, weil die
              // Seite keinen Entwurf haelt (experience-reviewer und Codex-P2 zu #548).
              if (hatEingaben) setVerwerfenFragen(true);
              else navigate(rueckweg);
            }}
          >
            Abbrechen
          </NebenKnopf>
          {/* GENAU EINE Hauptaktion (NFR-UX-004). Ein "Zuruecksetzen" daneben
              waere eine zweite Betonung fuer eine Handlung, die niemand sucht. */}
          {/* EINE Hauptaktion, und ihre Beschriftung sagt, was der naechste
              Druck TUT. Nach einer Warnung heisst sie "Trotzdem neu anlegen"
              -- das ist das "begruendet neu anlegen" aus CM-CAP-005, so weit
              dieser Schnitt es halten kann: eine bewusste, informierte zweite
              Handlung. Ein zweiter Knopf daneben ("Ignorieren", "Zusammen-
              fuehren") waere eine zweite Hauptaktion (NFR-UX-004) -- und
              "Zusammenfuehren" waere zusaetzlich eine Attrappe, denn Merge
              gehoert CM-CAP-008 und ist nicht gebaut. */}
          <Button appearance="primary" type="submit" disabled={busy}>
            {busy
              ? 'Wird angelegt …'
              : leitungFuerNeue && gewaehlteLeitung.length > 0
                ? leitungAnlegenLabel(gewaehlteLeitung.length, warningSeen, orgNeu)
                : gewaehlteLeitungVorhanden.length > 0
                  ? leitungAnlegenLabel(gewaehlteLeitungVorhanden.length, warningSeen, false)
                : createActionLabel(warningSeen, vorschauOrganisation?.neu === true, { create: CREATE_LABEL, createAnyway: CREATE_ANYWAY_LABEL })}
          </Button>
        </div>
        {/* Klein unter der Hauptaktion (DESIGN zu #548): wo der Kontakt sichtbar wird, oder bei
            mehreren Haeusern die Grenze dieses Schnitts. */}
        {hauslage === 'laedt' ? undefined : (
          <Caption1 className={anlegenStyles.hausSatz}>{hauslage === 'mehrere' ? MULTI_HOUSE_CAPTURE_NOTICE : captureVisibilityNotice(houseName, houseArt)}</Caption1>
        )}
      </form>
      {verwerfenFragen ? (
        <Dialog
          open
          modalType="alert"
          onOpenChange={(_event, data) => {
            if (!data.open) {
              setVerwerfenFragen(false);
              // Fluent gibt den Fokus bei bedingt eingehaengten Dialogen nicht zurueck.
              window.setTimeout(() => abbrechenRef.current?.focus(), 0);
            }
          }}
        >
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Eingaben verwerfen?</DialogTitle>
              <DialogContent>Was du hier eingegeben hast, wird nicht gespeichert.</DialogContent>
              <DialogActions>
                {/* Zuerst: Fluent fokussiert das erste Bedienelement, und der sichere Weg ist "weiter". */}
                <Button
                  appearance="primary"
                  onClick={() => {
                    setVerwerfenFragen(false);
                    window.setTimeout(() => abbrechenRef.current?.focus(), 0);
                  }}
                >
                  Weiter bearbeiten
                </Button>
                <NebenKnopf onClick={() => navigate(rueckweg)}>
                  Verwerfen
                </NebenKnopf>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      ) : undefined}
    </Card>
  );
}
